import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Toaster, toast } from "sonner";
import { useLocale } from "../../../locales";
import type {
  AssetItem,
  CanvasSettings,
  EditorElement,
  EditorState,
  ElementSize,
  GroupData,
} from "../../../editor/types";
import type {
  AudioStyle,
  TextStyle,
  ChoiceAppearanceExtended,
  ImageStyle,
  ElementFormState,
} from "../types";
import { DEFAULT_CANVAS_SETTINGS, useEditorState } from "../../../editor/state";
import { flattenGroups } from "../../../editor/grouping";
import { useToolbarShortcuts } from "../hooks/useToolbarShortcuts";
import {
  ApiService,
  apiService,
  type Lesson,
  type Grade,
  type Subject,
  type QuestionType,
  type Template,
  type SaveQuestionResponse,
  type TextToSpeechMedia,
} from "../../../services/api";
import {
  getElementLibrarySections,
  TEMPLATE_MIME,
  getQuestionTypeLibraryFilters,
} from "../constants";
import {
  cloneCanvasSettings,
  DEFAULT_EDITOR_CANVAS_HEIGHT,
  findNonOverlappingPosition,
  MAX_EDITOR_CANVAS_HEIGHT,
} from "../lib/canvas";
import { DEFAULT_AUDIO_STYLE } from "../lib/appearance";
import { generateElementId } from "../lib/ids";
import {
  createElementFromTemplate,
  createElementsFromTemplate,
} from "../lib/templates";
import {
  convertImageFileToWebp,
  createLocalCanvasMediaElement,
  getLocalCanvasMediaKind,
} from "../lib/localMedia";
import type { ElementTemplateKey } from "../types";
import {
  validateCanvasRequirements,
  validateAnswerKey,
} from "../lib/validation";
import { renderCanvasElementContent } from "../lib/renderers";
import { captureCanvasScreenshotBothFormats } from "../lib/canvasScreenshot";
import {
  loadFormElementVisibility,
  saveFormElementVisibility,
  type FormElementVisibilityMap,
} from "../lib/formElementVisibility";
import FormElementVisibilityModal from "./FormElementVisibilityModal";
import Toolbar from "./Toolbar";
import CanvasElement from "./CanvasElement";
import { ElementPropertiesPanel } from "./panels/ElementPropertiesPanel";
import InteractionPanel from "./panels/InteractionPanel";
import SettingsModal from "./SettingsModal";
import FullScreenPreview from "./FullScreenPreview";
import SettingsIcon from "./icons/SettingsIcon";
import EraserIcon from "./icons/EraserIcon";
import ContextMenu from "./ContextMenu";
import QuestionSettingsBar from "./QuestionSettingsBar";
import AnswerKeyModal from "./AnswerKeyModal";
import QuestionTypeChangeConfirmModal from "./QuestionTypeChangeConfirmModal";
import TemplatesModal from "./TemplatesModal";
import TemplateNameModal from "./TemplateNameModal";
import SaveSuccessModal from "./SaveSuccessModal";
import MathTypeEditorModal from "./MathTypeEditorModal";
import TextToSpeechModal, {
  type TextToSpeechSubmitPayload,
} from "./TextToSpeechModal";

console.log("DEBUG: Checking EditorLayout imports:");
console.log("ElementPropertiesPanel:", ElementPropertiesPanel);
console.log("Toolbar:", Toolbar);
console.log("CanvasElement:", CanvasElement);
console.log("InteractionPanel:", InteractionPanel);

const parseNumber = (value: unknown, fallback?: number): number => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  if (typeof fallback === "number") {
    return fallback;
  }
  return 0;
};

const stripHtmlToText = (value: string): string => {
  if (typeof window === "undefined") {
    return value.replace(/<[^>]*>/g, " ");
  }
  const container = document.createElement("div");
  container.innerHTML = value;
  return container.textContent || container.innerText || "";
};

const getElementSpeechText = (element: EditorElement | undefined): string => {
  if (!element || element.type !== "text") {
    return "";
  }

  const data = element.data as { content?: unknown; htmlContent?: unknown };
  const rawText =
    typeof data.content === "string" && data.content.trim().length > 0
      ? data.content
      : typeof data.htmlContent === "string"
        ? stripHtmlToText(data.htmlContent)
        : "";

  return rawText.replace(/\s+/g, " ").trim();
};

const extractMediaPath = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "";
  }

  try {
    const url = new URL(trimmedValue);
    return decodeURIComponent(url.pathname.replace(/^\/+/, ""));
  } catch {
    return trimmedValue.replace(/^\/+/, "");
  }
};

const getAudioElementMediaPath = (element: EditorElement | undefined): string => {
  if (!element || element.type !== "audio") {
    return "";
  }

  const data = element.data as {
    mediaPath?: unknown;
    path?: unknown;
    style?: { sourceUrl?: unknown };
  };

  if (typeof data.mediaPath === "string" && data.mediaPath.trim()) {
    return data.mediaPath.trim().replace(/^\/+/, "");
  }

  if (typeof data.path === "string" && data.path.trim()) {
    return data.path.trim().replace(/^\/+/, "");
  }

  const sourceUrl = data.style?.sourceUrl;
  return typeof sourceUrl === "string" ? extractMediaPath(sourceUrl) : "";
};

const canPermanentlyDeleteAudioElement = (
  element: EditorElement | undefined
): boolean => {
  if (!element || element.type !== "audio") {
    return false;
  }

  const data = element.data as {
    generatedFromTextElementId?: unknown;
    mediaId?: unknown;
    sceneMedia?: unknown;
    sourceFile?: unknown;
  };

  return Boolean(
    data.generatedFromTextElementId ||
      data.mediaId ||
      data.sceneMedia ||
      data.sourceFile
  );
};

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const numeric = parseNumber(value, NaN);
  return Number.isNaN(numeric) ? undefined : numeric;
};

const parseBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "yes") {
      return true;
    }
    if (normalized === "0" || normalized === "false" || normalized === "no") {
      return false;
    }
  }
  return fallback;
};

const normalizeMathElementType = (type: string): string =>
  type === "mathlive" ? "mathtype" : type;
const normalizeMathContentType = (value?: string): string | undefined =>
  value === "mathlive" ? "mathtype" : value;
const isMathTypeElement = (element?: EditorElement | null): boolean =>
  element?.type === "mathtype" || element?.type === "mathlive";

const PAINT_SWATCH_PREFIX = "paint-swatch:";
const PAINT_INTERSECTION_SWATCH_PREFIX = "paint-intersection-swatch:";

const DEFAULT_ELEMENT_BOUNDS_HEIGHT = 40;
const RULER_LABEL_INTERVAL = 100;

type CanvasGuideOrientation = "horizontal" | "vertical";
type CanvasGuidePreset = "margin-50" | "center" | "columns-3" | "rows-3";

interface CanvasGuide {
  id: string;
  orientation: CanvasGuideOrientation;
  position: number;
}

const isTextEntryTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, [contenteditable="true"], .ck-editor__editable'
    )
  );
};

const clampCanvasGuidePosition = (
  position: number,
  orientation: CanvasGuideOrientation,
  canvasSize: { width: number; height: number }
): number => {
  const limit = orientation === "vertical" ? canvasSize.width : canvasSize.height;
  return Math.max(0, Math.min(limit, Math.round(position)));
};

const getRulerLabelPositions = (length: number): number[] => {
  const labels: number[] = [];
  for (let position = 0; position <= length; position += RULER_LABEL_INTERVAL) {
    labels.push(position);
  }
  return labels;
};

const resolveElementSizeForBounds = (
  element: EditorElement,
  overrideSize?: Partial<ElementSize>
) => {
  const width = overrideSize?.width ?? element.size.width;
  const height =
    overrideSize?.height ??
    element.size.height ??
    element.size.width ??
    DEFAULT_ELEMENT_BOUNDS_HEIGHT;

  return { width, height };
};

const getRotatedVisualBounds = (
  position: { x: number; y: number },
  size: { width: number; height: number },
  rotation?: number
) => {
  const angle = ((rotation || 0) * Math.PI) / 180;
  const absCos = Math.abs(Math.cos(angle));
  const absSin = Math.abs(Math.sin(angle));
  const rotatedWidth = size.width * absCos + size.height * absSin;
  const rotatedHeight = size.width * absSin + size.height * absCos;
  const offsetX = (size.width - rotatedWidth) / 2;
  const offsetY = (size.height - rotatedHeight) / 2;

  return {
    left: position.x + offsetX,
    right: position.x + size.width - offsetX,
    top: position.y + offsetY,
    bottom: position.y + size.height - offsetY,
  };
};

const normalizeAnswerKey = (answerKey: any): Record<string, any> => {
  if (!answerKey || typeof answerKey !== "object") {
    return {};
  }

  const normalized: Record<string, any> = {};

  Object.keys(answerKey).forEach((elementId) => {
    // Guard against corrupted payloads with empty keys (breaks saving/updating)
    if (
      !elementId ||
      (typeof elementId === "string" && elementId.trim() === "")
    ) {
      return;
    }
    const value = answerKey[elementId];

    // If it's an array (for choice elements or order-list)
    if (Array.isArray(value)) {
      normalized[elementId] = value;
    }
    // If it's an object with acceptedItems (for drop-target)
    else if (value && typeof value === "object" && "acceptedItems" in value) {
      const config: any = {
        acceptedItems: Array.isArray(value.acceptedItems)
          ? value.acceptedItems
          : [],
      };

      if (
        value.minItems !== undefined &&
        value.minItems !== null &&
        value.minItems !== ""
      ) {
        config.minItems = parseNumber(value.minItems, undefined);
      }
      if (
        value.maxItems !== undefined &&
        value.maxItems !== null &&
        value.maxItems !== ""
      ) {
        config.maxItems = parseNumber(value.maxItems, undefined);
      }
      if (
        value.exactCount !== undefined &&
        value.exactCount !== null &&
        value.exactCount !== ""
      ) {
        config.exactCount = parseNumber(value.exactCount, undefined);
      }
      if (value.orderMatters !== undefined) {
        config.orderMatters = parseBoolean(value.orderMatters, false);
      }

      normalized[elementId] = config;
    }
    // If it's an object with connections (for matching)
    else if (value && typeof value === "object" && "connections" in value) {
      normalized[elementId] = value;
    }
    // If it's an object with blanks (for fill-in-the-blanks)
    else if (value && typeof value === "object" && "blanks" in value) {
      normalized[elementId] = value;
    }
    // If it's an object with paintSwatchId or paintColor (for paint-area)
    else if (
      value &&
      typeof value === "object" &&
      ("paintSwatchId" in value || "paintColor" in value)
    ) {
      normalized[elementId] = value;
    }
  });

  return normalized;
};

const normalizeQuestionTypeKey = (value: string): string => {
  if (!value) {
    return "";
  }

  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("tr-TR")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const findQuestionTypeFilterKey = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const rawKey = String(value).trim();
  if (!rawKey) {
    return null;
  }

  const filters = getQuestionTypeLibraryFilters();

  if (filters[rawKey]) {
    return rawKey;
  }

  const normalizedKey = normalizeQuestionTypeKey(rawKey);
  if (normalizedKey && filters[normalizedKey]) {
    return normalizedKey;
  }

  if (!normalizedKey) {
    return null;
  }

  const normalizedCompact = normalizedKey.replace(/-/g, "");
  const fallbackMatch = Object.keys(filters).find(
    (key) => {
      if (!key || key === "default") {
        return false;
      }
      const candidateCompact = key.replace(/-/g, "");
      return (
        normalizedCompact.includes(candidateCompact) ||
        candidateCompact.includes(normalizedCompact)
      );
    }
  );

  if (fallbackMatch) {
    return fallbackMatch;
  }

  const normalizedLower = normalizedKey.replace(/-/g, " ");
  const rawLower = rawKey.toLocaleLowerCase("tr-TR");

  const matchesDragDrop =
    /drag|drop|surukle|bırak|birak/.test(normalizedLower) ||
    /drag|drop|sürükle|surukle|bırak|birak/i.test(rawLower);
  if (matchesDragDrop) {
    return "drag-drop";
  }
  const matchesMultipleChoice =
    /choice|secme|seçme|coktan|çoktan|multiple|multi/.test(normalizedLower) ||
    /choice|seçme|secme|coktan|çoktan|multiple|multi/i.test(rawLower);
  if (matchesMultipleChoice) {
    return "multiple-choice";
  }

  const matchesPainting =
    /paint|boyama|boya/.test(normalizedLower) ||
    /paint|boyama|boya/i.test(rawLower);
  if (matchesPainting) {
    return "painting";
  }

  return null;
};

const normalizeQuestionPayload = (
  questionPayload: any
): {
  canvas: CanvasSettings;
  elements: EditorElement[];
  assets: AssetItem[];
} => {
  const canvasPayload = questionPayload?.canvas ?? {};
  const baseCanvas = cloneCanvasSettings(DEFAULT_CANVAS_SETTINGS);

  baseCanvas.width = parseNumber(canvasPayload.width, baseCanvas.width);
  baseCanvas.height = parseNumber(canvasPayload.height, baseCanvas.height);
  baseCanvas.background = canvasPayload.background || baseCanvas.background;
  baseCanvas.expandable = parseBoolean(
    canvasPayload.expandable,
    baseCanvas.expandable
  );
  baseCanvas.lockWidth = parseBoolean(
    canvasPayload.lockWidth,
    baseCanvas.lockWidth
  );
  baseCanvas.padding = {
    x: parseNumber(canvasPayload.padding?.x, baseCanvas.padding.x),
    y: parseNumber(canvasPayload.padding?.y, baseCanvas.padding.y),
  };

  baseCanvas.innerPadding = canvasPayload.innerPadding
    ? {
        top: parseNumber(canvasPayload.innerPadding.top, 0),
        right: parseNumber(canvasPayload.innerPadding.right, 0),
        bottom: parseNumber(canvasPayload.innerPadding.bottom, 0),
        left: parseNumber(canvasPayload.innerPadding.left, 0),
      }
    : baseCanvas.innerPadding;

  baseCanvas.grid = {
    enabled: parseBoolean(canvasPayload.grid?.enabled, baseCanvas.grid.enabled),
    size: parseNumber(canvasPayload.grid?.size, baseCanvas.grid.size),
  };

  baseCanvas.hiddenArea = {
    enabled: parseBoolean(
      canvasPayload.hiddenArea?.enabled,
      baseCanvas.hiddenArea.enabled
    ),
    size: parseNumber(
      canvasPayload.hiddenArea?.size,
      baseCanvas.hiddenArea.size
    ),
  };

  const questionMeta = questionPayload ?? {};
  const questionTypeIdFromPayload =
    questionMeta.questionType?.id ?? questionMeta.questionTypeId;
  const questionTypeNameFromPayload = questionMeta.questionType?.name ?? "";
  const questionType = questionTypeIdFromPayload
    ? {
        id: String(questionTypeIdFromPayload),
        name: String(questionTypeNameFromPayload || ""),
      }
    : undefined;

  const learningOutcomesValue = questionMeta.learningOutcomes
    ? questionMeta.learningOutcomes
    : Array.isArray(questionMeta.outcomeIds) &&
      questionMeta.outcomeIds.length > 0
    ? questionMeta.outcomeIds[0]
    : undefined;

  baseCanvas.question = {
    ...baseCanvas.question,
    questionType,
    grade: questionMeta.gradeId
      ? String(questionMeta.gradeId)
      : baseCanvas.question?.grade,
    lesson: questionMeta.lessonId
      ? String(questionMeta.lessonId)
      : baseCanvas.question?.lesson,
    subject: questionMeta.moduleId
      ? String(questionMeta.moduleId)
      : baseCanvas.question?.subject,
    topic: questionMeta.moduleId
      ? String(questionMeta.moduleId)
      : baseCanvas.question?.topic,
    learningOutcomes:
      learningOutcomesValue || baseCanvas.question?.learningOutcomes,
    difficulty:
      parseOptionalNumber(questionMeta.difficulty) ??
      baseCanvas.question?.difficulty,
    trackTime: parseBoolean(
      questionMeta.trackTime,
      baseCanvas.question?.trackTime ?? false
    ),
    notMobileFriendly: parseBoolean(
      questionMeta.notMobileFriendly ?? questionMeta.not_mobile_friendly,
      baseCanvas.question?.notMobileFriendly ?? false
    ),
    displayInMba: parseBoolean(
      questionMeta.displayInMba ?? questionMeta.display_in_mba,
      baseCanvas.question?.displayInMba ?? false
    ),
    timeLimitSeconds:
      parseOptionalNumber(questionMeta.timeLimitSeconds) ??
      baseCanvas.question?.timeLimitSeconds,
    solutionVideoUrl:
      questionMeta.solutionVideoUrl ?? baseCanvas.question?.solutionVideoUrl,
    topicVideoUrl:
      questionMeta.topicVideoUrl ?? baseCanvas.question?.topicVideoUrl,
    solutionTextUrl:
      questionMeta.solutionTextUrl ?? baseCanvas.question?.solutionTextUrl,
    answerKey:
      questionMeta.answerKey && typeof questionMeta.answerKey === "object"
        ? normalizeAnswerKey(questionMeta.answerKey)
        : baseCanvas.question?.answerKey,
    title: questionMeta.title || baseCanvas.question?.title,
    tags: Array.isArray(questionMeta.tags)
      ? questionMeta.tags
      : baseCanvas.question?.tags,
    questionCode:
      questionMeta.questionCode || baseCanvas.question?.questionCode,
  };

  const elementsPayload = questionMeta.elements ?? {};
  const parsedElements: EditorElement[] = Object.values(elementsPayload).map(
    (element: any, index: number) => {
      const sizePayload = element.size ?? {};
      const positionPayload = element.position ?? {};
      const size: ElementSize = {
        width: parseNumber(sizePayload.width, 200),
      };
      const height = parseOptionalNumber(sizePayload.height);
      if (height !== undefined) {
        size.height = height;
      }
      const minWidth = parseOptionalNumber(sizePayload.minWidth);
      if (minWidth !== undefined) {
        size.minWidth = minWidth;
      }
      const minHeight = parseOptionalNumber(sizePayload.minHeight);
      if (minHeight !== undefined) {
        size.minHeight = minHeight;
      }

      const elementType = normalizeMathElementType(String(element.type ?? ""));
      let elementData = element.data ?? {};

      if (elementType === "audio") {
        const stylePayload =
          (elementData as { style?: Partial<AudioStyle> }).style ?? {};
        const resolvedStyle: AudioStyle = {
          ...DEFAULT_AUDIO_STYLE,
          ...stylePayload,
          sourceUrl: stylePayload.sourceUrl ?? DEFAULT_AUDIO_STYLE.sourceUrl,
          label: stylePayload.label ?? DEFAULT_AUDIO_STYLE.label,
          autoPlay: parseBoolean(
            stylePayload.autoPlay,
            DEFAULT_AUDIO_STYLE.autoPlay
          ),
          loop: parseBoolean(stylePayload.loop, DEFAULT_AUDIO_STYLE.loop),
          showControls: parseBoolean(
            stylePayload.showControls,
            DEFAULT_AUDIO_STYLE.showControls
          ),
          showIcon: parseBoolean(
            stylePayload.showIcon,
            DEFAULT_AUDIO_STYLE.showIcon
          ),
          showLabel: parseBoolean(
            stylePayload.showLabel,
            DEFAULT_AUDIO_STYLE.showLabel
          ),
          backgroundColor:
            stylePayload.backgroundColor ?? DEFAULT_AUDIO_STYLE.backgroundColor,
          borderColor:
            stylePayload.borderColor ?? DEFAULT_AUDIO_STYLE.borderColor,
          borderWidth: parseNumber(
            stylePayload.borderWidth,
            DEFAULT_AUDIO_STYLE.borderWidth
          ),
          borderRadius: parseNumber(
            stylePayload.borderRadius,
            DEFAULT_AUDIO_STYLE.borderRadius
          ),
          textColor: stylePayload.textColor ?? DEFAULT_AUDIO_STYLE.textColor,
          iconColor: stylePayload.iconColor ?? DEFAULT_AUDIO_STYLE.iconColor,
          paddingX: parseNumber(
            stylePayload.paddingX,
            DEFAULT_AUDIO_STYLE.paddingX
          ),
          paddingY: parseNumber(
            stylePayload.paddingY,
            DEFAULT_AUDIO_STYLE.paddingY
          ),
        };

        elementData = {
          ...elementData,
          style: resolvedStyle,
        };
      }

      if (elementType === "text") {
        const stylePayload =
          (elementData as { style?: Partial<TextStyle> }).style ?? {};
        if (stylePayload && Object.keys(stylePayload).length > 0) {
          const resolvedStyle: Partial<TextStyle> = {
            ...stylePayload,
          };

          if (stylePayload.fontSize !== undefined) {
            resolvedStyle.fontSize = parseNumber(stylePayload.fontSize);
          }
          if (stylePayload.fontWeight !== undefined) {
            resolvedStyle.fontWeight = parseNumber(stylePayload.fontWeight);
          }
          if (stylePayload.italic !== undefined) {
            resolvedStyle.italic = parseBoolean(stylePayload.italic);
          }
          if (stylePayload.underline !== undefined) {
            resolvedStyle.underline = parseBoolean(stylePayload.underline);
          }
          if (stylePayload.lineHeight !== undefined) {
            resolvedStyle.lineHeight = parseNumber(stylePayload.lineHeight);
          }
          if (stylePayload.letterSpacing !== undefined) {
            resolvedStyle.letterSpacing = parseNumber(
              stylePayload.letterSpacing
            );
          }
          if (stylePayload.paddingX !== undefined) {
            resolvedStyle.paddingX = parseNumber(stylePayload.paddingX);
          }
          if (stylePayload.paddingY !== undefined) {
            resolvedStyle.paddingY = parseNumber(stylePayload.paddingY);
          }
          if (stylePayload.borderRadius !== undefined) {
            resolvedStyle.borderRadius = parseNumber(stylePayload.borderRadius);
          }
          if (stylePayload.borderWidth !== undefined) {
            resolvedStyle.borderWidth = parseNumber(stylePayload.borderWidth);
          }

          elementData = {
            ...elementData,
            style: resolvedStyle,
          };
        }
      }

      if (elementType === "image") {
        const stylePayload =
          (elementData as { style?: Partial<ImageStyle> }).style ?? {};
        if (stylePayload && Object.keys(stylePayload).length > 0) {
          const resolvedStyle: Partial<ImageStyle> = {
            ...stylePayload,
          };

          if (stylePayload.borderRadius !== undefined) {
            resolvedStyle.borderRadius = parseNumber(stylePayload.borderRadius);
          }
          if (stylePayload.borderWidth !== undefined) {
            resolvedStyle.borderWidth = parseNumber(stylePayload.borderWidth);
          }
          if (stylePayload.opacity !== undefined) {
            resolvedStyle.opacity = parseNumber(stylePayload.opacity);
          }

          elementData = {
            ...elementData,
            style: resolvedStyle,
          };
        }
      }

      if (elementType === "choice") {
        // Parse data.multiple (çoklu seçim)
        const multipleValue = (elementData as any).multiple;
        if (multipleValue !== undefined) {
          elementData = {
            ...elementData,
            multiple: parseBoolean(multipleValue),
          };
        }

        // Parse options array
        const optionsArray = (elementData as any).options;
        if (Array.isArray(optionsArray)) {
          const parsedOptions = optionsArray.map((option: any) => {
            const normalizedContentType = normalizeMathContentType(
              option.contentType
            );
            const hasMathContent =
              (typeof option.mathml === "string" &&
                option.mathml.trim().length > 0) ||
              (typeof option.latex === "string" &&
                option.latex.trim().length > 0);
            const parsedOption: any = {
              ...option,
              contentType:
                normalizedContentType === "text" || !normalizedContentType
                  ? hasMathContent
                    ? "mathtype"
                    : "text"
                  : normalizedContentType,
            };

            if (option.disabled !== undefined) {
              parsedOption.disabled = parseBoolean(option.disabled);
            }
            if (option.imageWidth !== undefined) {
              parsedOption.imageWidth = parseNumber(option.imageWidth);
            }
            if (option.imageHeight !== undefined) {
              parsedOption.imageHeight = parseNumber(option.imageHeight);
            }
            if (option.imageBorderRadius !== undefined) {
              parsedOption.imageBorderRadius = parseNumber(
                option.imageBorderRadius
              );
            }

            return parsedOption;
          });

          elementData = {
            ...elementData,
            options: parsedOptions,
          };
        }

        // Parse appearance
        const appearancePayload =
          (elementData as { appearance?: Partial<ChoiceAppearanceExtended> })
            .appearance ?? {};
        if (appearancePayload && Object.keys(appearancePayload).length > 0) {
          const resolvedAppearance: Partial<ChoiceAppearanceExtended> = {
            ...appearancePayload,
          };

          if (appearancePayload.fontSize !== undefined) {
            resolvedAppearance.fontSize = parseNumber(
              appearancePayload.fontSize
            );
          }
          if (appearancePayload.fontWeight !== undefined) {
            resolvedAppearance.fontWeight = parseNumber(
              appearancePayload.fontWeight
            );
          }
          if (appearancePayload.gridColumns !== undefined) {
            resolvedAppearance.gridColumns = parseNumber(
              appearancePayload.gridColumns
            );
          }
          if (appearancePayload.groupBorderRadius !== undefined) {
            resolvedAppearance.groupBorderRadius = parseNumber(
              appearancePayload.groupBorderRadius
            );
          }
          if (appearancePayload.groupBorderWidth !== undefined) {
            resolvedAppearance.groupBorderWidth = parseNumber(
              appearancePayload.groupBorderWidth
            );
          }
          if (appearancePayload.groupPadding !== undefined) {
            resolvedAppearance.groupPadding = parseNumber(
              appearancePayload.groupPadding
            );
          }
          if (appearancePayload.optionBorderWidth !== undefined) {
            resolvedAppearance.optionBorderWidth = parseNumber(
              appearancePayload.optionBorderWidth
            );
          }
          if (appearancePayload.optionBorderRadius !== undefined) {
            resolvedAppearance.optionBorderRadius = parseNumber(
              appearancePayload.optionBorderRadius
            );
          }
          if (appearancePayload.optionPaddingX !== undefined) {
            resolvedAppearance.optionPaddingX = parseNumber(
              appearancePayload.optionPaddingX
            );
          }
          if (appearancePayload.optionPaddingY !== undefined) {
            resolvedAppearance.optionPaddingY = parseNumber(
              appearancePayload.optionPaddingY
            );
          }
          if (appearancePayload.optionSpacing !== undefined) {
            resolvedAppearance.optionSpacing = parseNumber(
              appearancePayload.optionSpacing
            );
          }
          if (appearancePayload.showIndicator !== undefined) {
            resolvedAppearance.showIndicator = parseBoolean(
              appearancePayload.showIndicator
            );
          }
          if (appearancePayload.indicatorSize !== undefined) {
            resolvedAppearance.indicatorSize = parseNumber(
              appearancePayload.indicatorSize
            );
          }
          if (appearancePayload.indicatorBorderWidth !== undefined) {
            resolvedAppearance.indicatorBorderWidth = parseNumber(
              appearancePayload.indicatorBorderWidth
            );
          }
          if (appearancePayload.indicatorBorderRadius !== undefined) {
            resolvedAppearance.indicatorBorderRadius = parseNumber(
              appearancePayload.indicatorBorderRadius
            );
          }
          if (appearancePayload.showOptionIcon !== undefined) {
            resolvedAppearance.showOptionIcon = parseBoolean(
              appearancePayload.showOptionIcon
            );
          }
          if (appearancePayload.optionIconSize !== undefined) {
            resolvedAppearance.optionIconSize = parseNumber(
              appearancePayload.optionIconSize
            );
          }
          if (appearancePayload.optionImageSize !== undefined) {
            resolvedAppearance.optionImageSize = parseNumber(
              appearancePayload.optionImageSize
            );
          }
          if (appearancePayload.optionImageBorderRadius !== undefined) {
            resolvedAppearance.optionImageBorderRadius = parseNumber(
              appearancePayload.optionImageBorderRadius
            );
          }

          elementData = {
            ...elementData,
            appearance: resolvedAppearance,
          };
        }
      }

      // Preserve empty strings for drag-source and drop-target labels/placeholders
      // Add visibility flags for backward compatibility with old elements
      if (element.type === "drag-source") {
        // If label exists in data (even if null), preserve it as string
        if ("label" in elementData) {
          elementData = {
            ...elementData,
            label: elementData.label == null ? "" : String(elementData.label),
          };
        }
        // Convert showLabel to boolean (backend might send 1/0/"0"/"1"/true/false)
        if ("showLabel" in elementData) {
          const raw = elementData.showLabel;
          // Handle: 0, "0", false, "", null, undefined -> false
          // Handle: 1, "1", true, any other truthy value -> true
          const boolValue =
            raw === 0 ||
            raw === "0" ||
            raw === false ||
            raw === "" ||
            raw == null
              ? false
              : true;
          elementData = {
            ...elementData,
            showLabel: boolValue,
          };
        } else {
          // If showLabel is not explicitly set in data, use appearance.showLabel value
          const appearance = elementData.appearance as any;
          if (appearance && "showLabel" in appearance) {
            elementData = {
              ...elementData,
              showLabel: appearance.showLabel !== false,
            };
          }
        }
        // Convert clonable to boolean (backend might send 1/0/"0"/"1"/true/false)
        if ("clonable" in elementData) {
          const raw = elementData.clonable;
          // Handle: 0, "0", false, "", null, undefined -> false
          // Handle: 1, "1", true, any other truthy value -> true
          const boolValue =
            raw === 0 ||
            raw === "0" ||
            raw === false ||
            raw === "" ||
            raw == null
              ? false
              : Boolean(raw);
          elementData = {
            ...elementData,
            clonable: boolValue,
          };
        } else {
          // If clonable is not explicitly set in data, use appearance.clonable value
          const appearance = elementData.appearance as any;
          if (appearance && "clonable" in appearance) {
            elementData = {
              ...elementData,
              clonable: appearance.clonable === true,
            };
          }
        }
      }

      if (element.type === "drop-target") {
        // If label or placeholder exist in data (even if null), preserve them as strings
        const updates: Record<string, unknown> = {};
        if ("label" in elementData) {
          updates.label =
            elementData.label == null ? "" : String(elementData.label);
        }
        if ("placeholder" in elementData) {
          updates.placeholder =
            elementData.placeholder == null
              ? ""
              : String(elementData.placeholder);
        }
        // Convert showLabel and showPlaceholder to boolean (backend might send 1/0/"0"/"1"/true/false)
        if ("showLabel" in elementData) {
          const raw = elementData.showLabel;
          // Handle: 0, "0", false, "", null, undefined -> false
          // Handle: 1, "1", true, any other truthy value -> true
          updates.showLabel =
            raw === 0 ||
            raw === "0" ||
            raw === false ||
            raw === "" ||
            raw == null
              ? false
              : true;
        }
        if ("showPlaceholder" in elementData) {
          const raw = elementData.showPlaceholder;
          // Handle: 0, "0", false, "", null, undefined -> false
          // Handle: 1, "1", true, any other truthy value -> true
          updates.showPlaceholder =
            raw === 0 ||
            raw === "0" ||
            raw === false ||
            raw === "" ||
            raw == null
              ? false
              : true;
        }
        // Note: We don't set default values here anymore
        // Templates will set showLabel/showPlaceholder: true for new elements
        if (Object.keys(updates).length > 0) {
          elementData = {
            ...elementData,
            ...updates,
          };
        }
      }

      if (element.type === "order-list") {
        // Convert showLabel to boolean (backend might send 1/0/"0"/"1"/true/false)
        if ("showLabel" in elementData) {
          const raw = elementData.showLabel;
          // Handle: 0, "0", false, "", null, undefined -> false
          // Handle: 1, "1", true, any other truthy value -> true
          const boolValue =
            raw === 0 ||
            raw === "0" ||
            raw === false ||
            raw === "" ||
            raw == null
              ? false
              : true;
          elementData = {
            ...elementData,
            showLabel: boolValue,
          };
        }
        // Convert showNumbers to boolean (backend might send 1/0/"0"/"1"/true/false)
        const appearance = elementData.appearance as any;
        if (appearance && "showNumbers" in appearance) {
          const raw = appearance.showNumbers;
          const boolValue =
            raw === 0 ||
            raw === "0" ||
            raw === false ||
            raw === "" ||
            raw == null
              ? false
              : true;
          elementData = {
            ...elementData,
            appearance: {
              ...appearance,
              showNumbers: boolValue,
            },
          };
        }
        const appearancePayload =
          (elementData as { appearance?: Record<string, unknown> }).appearance ??
          {};
        if (Object.keys(appearancePayload).length > 0) {
          const resolvedAppearance: Record<string, unknown> = {
            ...appearancePayload,
          };
          const numericFields = [
            "borderWidth",
            "borderRadius",
            "paddingX",
            "paddingY",
            "labelPaddingX",
            "labelPaddingY",
            "labelBorderRadius",
            "itemSpacing",
            "itemBorderWidth",
            "itemBorderRadius",
            "itemPaddingX",
            "itemPaddingY",
            "itemFontSize",
            "backgroundImageOpacity",
          ];
          numericFields.forEach((field) => {
            if (field in appearancePayload) {
              const parsed = parseOptionalNumber(
                (appearancePayload as any)[field]
              );
              if (parsed !== undefined) {
                resolvedAppearance[field] = parsed;
              }
            }
          });
          if ("showNumbers" in appearancePayload) {
            resolvedAppearance.showNumbers = parseBoolean(
              (appearancePayload as any).showNumbers
            );
          }
          if ("useBackgroundImage" in appearancePayload) {
            resolvedAppearance.useBackgroundImage = parseBoolean(
              (appearancePayload as any).useBackgroundImage
            );
          }
          elementData = {
            ...elementData,
            appearance: resolvedAppearance,
          };
        }
        const itemsPayload = (elementData as { items?: Array<any> }).items;
        if (Array.isArray(itemsPayload)) {
          const parsedItems = itemsPayload.map((item) => ({
            ...item,
            frameWidth: parseOptionalNumber(item.frameWidth),
            frameHeight: parseOptionalNumber(item.frameHeight),
            imageWidth: parseOptionalNumber(item.imageWidth),
            imageHeight: parseOptionalNumber(item.imageHeight),
            imageBorderRadius: parseOptionalNumber(item.imageBorderRadius),
          }));
          elementData = {
            ...elementData,
            items: parsedItems,
          };
        }
      }

      if (element.type === "matching-point") {
        // Ensure pointType is preserved correctly (source or target)
        const pointType = (elementData as { pointType?: "source" | "target" })
          .pointType;
        if (pointType && (pointType === "source" || pointType === "target")) {
          elementData = {
            ...elementData,
            pointType: pointType,
          };
        } else {
          // Default to source if pointType is missing or invalid
          elementData = {
            ...elementData,
            pointType: "source" as const,
          };
        }

        // Ensure position is explicitly preserved (x, y coordinates)
        // Position is already parsed above, but we ensure it's in the data if needed
        // Position is stored at element level, not in data, so this is just for validation
      }

      if (element.type === "fill-in-the-blanks") {
        // Normalize fill-in-the-blanks appearance values
        const appearancePayload =
          (elementData as { appearance?: Partial<any> }).appearance ?? {};
        if (appearancePayload && Object.keys(appearancePayload).length > 0) {
          const resolvedAppearance: Partial<any> = {
            ...appearancePayload,
          };

          // Normalize boolean values (italic, underline)
          if (appearancePayload.italic !== undefined) {
            resolvedAppearance.italic = parseBoolean(
              appearancePayload.italic,
              false
            );
          }
          if (appearancePayload.underline !== undefined) {
            resolvedAppearance.underline = parseBoolean(
              appearancePayload.underline,
              false
            );
          }

          // Normalize inputBorderStyle (ensure it's 'full' or 'bottom', not 'dashed' or other values)
          if (appearancePayload.inputBorderStyle !== undefined) {
            const borderStyle = String(appearancePayload.inputBorderStyle);
            // If backend sends 'dashed' or invalid value, default to 'full'
            resolvedAppearance.inputBorderStyle =
              borderStyle === "full" || borderStyle === "bottom"
                ? borderStyle
                : "full";
          }

          elementData = {
            ...elementData,
            appearance: resolvedAppearance,
          };
        }
      }

      if (element.type === "push-to-talk") {
        // Normalize push-to-talk appearance values
        const appearancePayload =
          (elementData as { appearance?: Partial<any> }).appearance ?? {};
        if (appearancePayload && Object.keys(appearancePayload).length > 0) {
          const resolvedAppearance: Partial<any> = {
            ...appearancePayload,
          };

          // Normalize boolean values (backend sends "0"/"1" strings)
          const booleanFields = [
            "showTranscription",
            "showMicButton",
            "showErrorAlert",
            "showManualInput",
            "showManualInputLabel",
          ];
          booleanFields.forEach((field) => {
            if (field in appearancePayload) {
              resolvedAppearance[field] = parseBoolean(
                appearancePayload[field],
                true
              );
            }
          });

          // Normalize numeric values (backend might send strings)
          const numericFields = [
            "maxDurationSeconds",
            "borderWidth",
            "borderRadius",
            "buttonSizePx",
            "buttonContentSizePx",
            "buttonTextFontSize",
            "buttonCornerRadiusPx",
            "buttonBorderWidthPx",
            "buttonPaddingPx",
          ];
          numericFields.forEach((field) => {
            if (field in appearancePayload) {
              const numValue = parseOptionalNumber(appearancePayload[field]);
              if (numValue !== undefined) {
                resolvedAppearance[field] = numValue;
              }
            }
          });

          elementData = {
            ...elementData,
            appearance: resolvedAppearance,
          };
        }
      }

      if (elementType === "table") {
        // Normalize table data to ensure cells and cellElements are properly structured
        const tableData = elementData as {
          rows?: number;
          columns?: number;
          cells?: any[];
          cellElements?: Record<string, any>;
          rowHeights?: number[];
          columnWidths?: number[];
          appearance?: any;
          locked?: boolean;
        };

        // Ensure cellElements exists and is an object
        const cellElements = tableData.cellElements ?? {};

        // Ensure each cellElement is properly structured
        const normalizedCellElements: Record<string, any> = {};
        Object.keys(cellElements).forEach((elemId) => {
          const cellElement = cellElements[elemId];
          if (cellElement && typeof cellElement === "object") {
            const normalizedCellType = normalizeMathElementType(
              String(cellElement.type ?? "")
            );
            normalizedCellElements[elemId] = {
              ...cellElement,
              id: String(cellElement.id ?? elemId),
              type: normalizedCellType,
              position: {
                x: parseNumber(cellElement.position?.x, 0),
                y: parseNumber(cellElement.position?.y, 0),
              },
              size: {
                width: parseNumber(cellElement.size?.width, 100),
                height:
                  cellElement.size?.height !== undefined
                    ? parseNumber(cellElement.size.height, 50)
                    : undefined,
              },
              zIndex: parseNumber(cellElement.zIndex, 1),
              data: cellElement.data ?? {},
            };
          }
        });

        // Ensure cells array exists and each cell has elements array
        const cells = Array.isArray(tableData.cells) ? tableData.cells : [];
        const normalizedCells = cells.map((cell: any) => ({
          ...cell,
          id: String(cell.id ?? `cell-${cell.row}-${cell.col}`),
          row: parseNumber(cell.row, 0),
          col: parseNumber(cell.col, 0),
          elements: Array.isArray(cell.elements) ? cell.elements : [],
          backgroundColor: cell.backgroundColor,
          colSpan:
            cell.colSpan !== undefined
              ? parseNumber(cell.colSpan, 1)
              : undefined,
          rowSpan:
            cell.rowSpan !== undefined
              ? parseNumber(cell.rowSpan, 1)
              : undefined,
        }));

        elementData = {
          ...tableData,
          rows: parseNumber(tableData.rows, 3),
          columns: parseNumber(tableData.columns, 3),
          cells: normalizedCells,
          cellElements: normalizedCellElements,
          rowHeights: Array.isArray(tableData.rowHeights)
            ? tableData.rowHeights
            : undefined,
          columnWidths: Array.isArray(tableData.columnWidths)
            ? tableData.columnWidths
            : undefined,
          locked: parseBoolean(tableData.locked, false),
        };
      }

      return {
        id: String(element.id),
        type: elementType as EditorElement["type"],
        position: {
          x: parseNumber(positionPayload.x, 0),
          y: parseNumber(positionPayload.y, 0),
        },
        size,
        rotation: parseOptionalNumber(element.rotation),
        zIndex: parseNumber(element.zIndex, index + 1),
        appearOrder: parseOptionalNumber(element.appearOrder),
        appearDelayMs: parseOptionalNumber(element.appearDelayMs),
        minDisplayMs: parseOptionalNumber(element.minDisplayMs),
        data: elementData,
      } as EditorElement;
    }
  );

  parsedElements.sort((a, b) => a.zIndex - b.zIndex);

  // Build matching answer key from element connections if not already in answerKey
  const existingAnswerKey = baseCanvas.question?.answerKey || {};
  const hasMatchingAnswerKey = Object.entries(existingAnswerKey).some(
    ([key, value]) => {
      return (
        key === "matching-connections" ||
        (!Array.isArray(value) &&
          !("acceptedItems" in value) &&
          "connections" in value)
      );
    }
  );

  if (!hasMatchingAnswerKey) {
    // Build connections from source point elements
    const sourcePoints = parsedElements.filter(
      (el) =>
        el.type === "matching-point" &&
        (el.data as { pointType?: "source" | "target" })?.pointType === "source"
    );

    if (sourcePoints.length > 0) {
      const connections: Array<{
        sourcePointId: string;
        targetPointId: string;
      }> = [];

      sourcePoints.forEach((sourcePoint) => {
        const sourceData = sourcePoint.data as { connections?: string[] };
        const connectionsList = sourceData.connections || [];
        connectionsList.forEach((targetId) => {
          // Verify target point exists
          const targetPoint = parsedElements.find(
            (el) =>
              el.id === targetId &&
              el.type === "matching-point" &&
              (el.data as { pointType?: "source" | "target" })?.pointType ===
                "target"
          );
          if (targetPoint) {
            connections.push({
              sourcePointId: sourcePoint.id,
              targetPointId: targetId,
            });
          }
        });
      });

      if (connections.length > 0) {
        baseCanvas.question = {
          ...baseCanvas.question,
          answerKey: {
            ...existingAnswerKey,
            "matching-connections": { connections },
          },
        };
      }
    }
  } else {
    // If answerKey already has matching connections, sync them back to element.data
    console.log("🔍 [DEBUG] existingAnswerKey:", existingAnswerKey);
    const matchingAnswerKey = Object.entries(existingAnswerKey).find(
      ([key, value]) => {
        return (
          key === "matching-connections" ||
          (!Array.isArray(value) &&
            !("acceptedItems" in value) &&
            "connections" in value)
        );
      }
    );

    console.log("🔍 [DEBUG] matchingAnswerKey found:", matchingAnswerKey);

    if (matchingAnswerKey) {
      const [, matchingConfig] = matchingAnswerKey;
      if (
        matchingConfig &&
        !Array.isArray(matchingConfig) &&
        "connections" in matchingConfig
      ) {
        const connections = (
          matchingConfig as {
            connections: Array<{
              sourcePointId: string;
              targetPointId: string;
            }>;
          }
        ).connections;

        console.log("🔍 [DEBUG] connections from answerKey:", connections);

        // Get all source and target points from parsed elements
        const sourcePoints = parsedElements.filter(
          (el) =>
            el.type === "matching-point" &&
            (el.data as { pointType?: "source" | "target" })?.pointType ===
              "source"
        );
        const targetPoints = parsedElements.filter(
          (el) =>
            el.type === "matching-point" &&
            (el.data as { pointType?: "source" | "target" })?.pointType ===
              "target"
        );

        console.log(
          "🔍 [DEBUG] Current source points:",
          sourcePoints.map((p) => p.id)
        );
        console.log(
          "🔍 [DEBUG] Current target points:",
          targetPoints.map((p) => p.id)
        );

        // Check if any connection IDs exist in current elements
        const hasMatchingIds = connections.some(
          (conn) =>
            parsedElements.some((el) => el.id === String(conn.sourcePointId)) &&
            parsedElements.some((el) => el.id === String(conn.targetPointId))
        );

        console.log("🔍 [DEBUG] hasMatchingIds:", hasMatchingIds);

        // Safety check: If the number of connections doesn't match the number of points, skip loading
        const uniqueOldSourceIds = Array.from(
          new Set(connections.map((c) => String(c.sourcePointId)))
        );
        const uniqueOldTargetIds = Array.from(
          new Set(connections.map((c) => String(c.targetPointId)))
        );

        if (
          uniqueOldSourceIds.length !== sourcePoints.length ||
          uniqueOldTargetIds.length !== targetPoints.length
        ) {
          console.warn(
            `⚠️ [WARNING] Connection count mismatch! ` +
              `answerKey has ${uniqueOldSourceIds.length} sources and ${uniqueOldTargetIds.length} targets, ` +
              `but canvas has ${sourcePoints.length} sources and ${targetPoints.length} targets. ` +
              `Skipping connection loading to prevent incorrect mapping.`
          );
          // Skip connection loading if counts don't match - leave connections empty
        } else if (hasMatchingIds) {
          // IDs match - use direct mapping
          const connectionsBySource = new Map<string, string[]>();
          connections.forEach((conn) => {
            const sourceId = String(conn.sourcePointId);
            const targetId = String(conn.targetPointId);
            if (!connectionsBySource.has(sourceId)) {
              connectionsBySource.set(sourceId, []);
            }
            connectionsBySource.get(sourceId)!.push(targetId);
          });

          console.log(
            "🔍 [DEBUG] Direct mapping - connectionsBySource:",
            Array.from(connectionsBySource.entries())
          );

          parsedElements.forEach((element) => {
            const elementId = String(element.id);
            if (
              element.type === "matching-point" &&
              (element.data as { pointType?: "source" | "target" })
                ?.pointType === "source"
            ) {
              const elementConnections = connectionsBySource.get(elementId);
              console.log(
                `🔍 [DEBUG] Updating source point ${elementId} with connections:`,
                elementConnections
              );
              element.data = {
                ...element.data,
                connections: elementConnections || [],
              };
            }
          });
        } else {
          // IDs don't match - use position-based mapping (fallback for ID mismatch)
          console.log(
            "⚠️ [WARNING] ID mismatch detected! Using position-based mapping..."
          );

          // Sort source and target points by position (top-to-bottom, left-to-right)
          const sortedSources = [...sourcePoints].sort((a, b) => {
            if (Math.abs(a.position.y - b.position.y) > 20) {
              return a.position.y - b.position.y;
            }
            return a.position.x - b.position.x;
          });

          const sortedTargets = [...targetPoints].sort((a, b) => {
            if (Math.abs(a.position.y - b.position.y) > 20) {
              return a.position.y - b.position.y;
            }
            return a.position.x - b.position.x;
          });

          console.log(
            "🔍 [DEBUG] Sorted sources:",
            sortedSources.map((p) => ({ id: p.id, pos: p.position }))
          );
          console.log(
            "🔍 [DEBUG] Sorted targets:",
            sortedTargets.map((p) => ({ id: p.id, pos: p.position }))
          );

          // Map old IDs to new IDs based on position
          const oldToNewSourceMap = new Map<string, string>();
          const oldToNewTargetMap = new Map<string, string>();

          // Extract unique old source and target IDs from connections
          const oldSourceIds = Array.from(
            new Set(connections.map((c) => String(c.sourcePointId)))
          );
          const oldTargetIds = Array.from(
            new Set(connections.map((c) => String(c.targetPointId)))
          );

          console.log("🔍 [DEBUG] Old source IDs:", oldSourceIds);
          console.log("🔍 [DEBUG] Old target IDs:", oldTargetIds);

          // Map by index (assuming same order)
          oldSourceIds.forEach((oldId, index) => {
            if (sortedSources[index]) {
              oldToNewSourceMap.set(oldId, sortedSources[index].id);
            }
          });

          oldTargetIds.forEach((oldId, index) => {
            if (sortedTargets[index]) {
              oldToNewTargetMap.set(oldId, sortedTargets[index].id);
            }
          });

          console.log(
            "🔍 [DEBUG] Old-to-new source map:",
            Array.from(oldToNewSourceMap.entries())
          );
          console.log(
            "🔍 [DEBUG] Old-to-new target map:",
            Array.from(oldToNewTargetMap.entries())
          );

          // Rebuild connections with new IDs
          const connectionsBySource = new Map<string, string[]>();
          connections.forEach((conn) => {
            const oldSourceId = String(conn.sourcePointId);
            const oldTargetId = String(conn.targetPointId);
            const newSourceId = oldToNewSourceMap.get(oldSourceId);
            const newTargetId = oldToNewTargetMap.get(oldTargetId);

            if (newSourceId && newTargetId) {
              if (!connectionsBySource.has(newSourceId)) {
                connectionsBySource.set(newSourceId, []);
              }
              connectionsBySource.get(newSourceId)!.push(newTargetId);
              console.log(
                `🔍 [DEBUG] Mapped: ${oldSourceId} → ${newSourceId}, ${oldTargetId} → ${newTargetId}`
              );
            } else {
              console.warn(
                `⚠️ [WARNING] Failed to map connection: ${oldSourceId} → ${oldTargetId}`
              );
            }
          });

          console.log(
            "🔍 [DEBUG] Position-based mapping - connectionsBySource:",
            Array.from(connectionsBySource.entries())
          );

          // Update elements with mapped connections
          parsedElements.forEach((element) => {
            const elementId = String(element.id);
            if (
              element.type === "matching-point" &&
              (element.data as { pointType?: "source" | "target" })
                ?.pointType === "source"
            ) {
              const elementConnections = connectionsBySource.get(elementId);
              console.log(
                `🔍 [DEBUG] Updating source point ${elementId} with mapped connections:`,
                elementConnections
              );
              element.data = {
                ...element.data,
                connections: elementConnections || [],
              };
            }
          });
        }

        // Clear connections from target points (they shouldn't have connections)
        parsedElements.forEach((element) => {
          if (
            element.type === "matching-point" &&
            (element.data as { pointType?: "source" | "target" })?.pointType ===
              "target"
          ) {
            const { connections: _, ...restData } = element.data as {
              connections?: string[];
            };
            element.data = restData;
          }
        });

        console.log(
          "🔍 [DEBUG] parsedElements after connections update:",
          parsedElements.filter((el) => el.type === "matching-point")
        );
      }
    }
  }

  const assetsPayload = questionMeta.assets ?? {};
  const parsedAssets: AssetItem[] = Object.entries(assetsPayload).map(
    ([assetId, assetValue]: [string, any]) => ({
      id: assetId,
      name: assetValue?.name || assetId,
      type: assetValue?.type || "image",
      meta: assetValue?.meta,
    })
  );

  return {
    canvas: baseCanvas,
    elements: parsedElements,
    assets: parsedAssets,
  };
};

interface PreviewOnlyScreenProps {
  canvas: CanvasSettings;
  elements: EditorElement[];
  hiddenElements: Set<string>;
  isLoading: boolean;
  error: string | null;
  missingQuestion: boolean;
}

const PreviewOnlyScreen: React.FC<PreviewOnlyScreenProps> = ({
  canvas,
  elements,
  hiddenElements,
  isLoading,
  error,
  missingQuestion,
}) => {
  const { t } = useLocale();
  // Form state for drag-drop interactions
  const [formState, setFormState] = useState<Record<string, string | string[]>>(
    {}
  );
  const [matchingConnections, setMatchingConnections] = useState<
    Record<string, string[]>
  >({});
  const [draggingSource, setDraggingSource] = useState<{
    sourceId: string;
    sourcePos: { x: number; y: number };
    mousePos: { x: number; y: number };
  } | null>(null);
  const [mousePos, setMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [snappedTarget, setSnappedTarget] = useState<string | null>(null);
  const [hoveredConnection, setHoveredConnection] = useState<string | null>(
    null
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Initialize matchingConnections as empty - preview should start fresh like a student's view
  useEffect(() => {
    setMatchingConnections({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  // Handle matching point updates
  const handleMatchingUpdate = useCallback(
    (elementId: string, changes: Partial<any>) => {
      const connections =
        (changes.data as any)?.connections ?? changes.connections;
      if (connections !== undefined) {
        setMatchingConnections((prev) => ({
          ...prev,
          [elementId]: Array.isArray(connections) ? connections : [],
        }));
      }
    },
    []
  );

  const handlePaintAreaUpdate = useCallback(
    (elementId: string, changes: Partial<any>) => {
      const paintedColor = (changes as { paintedColor?: string }).paintedColor;
      const paintSwatchId = (changes as { paintSwatchId?: string | null })
        .paintSwatchId;
      const intersectionKey = (changes as { intersectionKey?: string })
        .intersectionKey;
      const intersectionColor = (changes as { intersectionColor?: string })
        .intersectionColor;
      const intersectionSwatchId = (
        changes as { intersectionSwatchId?: string | null }
      ).intersectionSwatchId;
      setFormState((prev) => {
        const next = { ...prev };
        if (paintedColor !== undefined) {
          next[elementId] = paintedColor;
        }
        if (paintSwatchId !== undefined) {
          const key = `${PAINT_SWATCH_PREFIX}${elementId}`;
          if (paintSwatchId) {
            next[key] = paintSwatchId;
          } else {
            delete next[key];
          }
        }
        if (intersectionKey && intersectionColor) {
          next[`paint-intersection:${intersectionKey}`] = intersectionColor;
        }
        if (intersectionKey && intersectionSwatchId !== undefined) {
          const key = `${PAINT_INTERSECTION_SWATCH_PREFIX}${intersectionKey}`;
          if (intersectionSwatchId) {
            next[key] = intersectionSwatchId;
          } else {
            delete next[key];
          }
        }
        return next;
      });
    },
    []
  );

  const handleElementUpdate = useCallback(
    (elementId: string, changes: Partial<any>) => {
      const element = elements.find((el) => el.id === elementId);
      if (element?.type === "matching-point") {
        handleMatchingUpdate(elementId, changes);
        return;
      }
      if (element?.type === "paint-area") {
        handlePaintAreaUpdate(elementId, changes);
      }
    },
    [elements, handleMatchingUpdate, handlePaintAreaUpdate]
  );

  // Drag başladığında source point bilgisini al
  useEffect(() => {
    const handleMatchingDragStart = (e: CustomEvent) => {
      const { sourceId, sourcePos, mousePos } = e.detail;
      setDraggingSource({
        sourceId,
        sourcePos,
        mousePos: mousePos || sourcePos,
      });
    };

    window.addEventListener(
      "matching-drag-start",
      handleMatchingDragStart as EventListener
    );
    return () => {
      window.removeEventListener(
        "matching-drag-start",
        handleMatchingDragStart as EventListener
      );
    };
  }, []);

  // Drag bittiğinde bağlantıyı kur
  useEffect(() => {
    const handleDragEnd = () => {
      if (draggingSource && snappedTarget) {
        const sourcePoint = elements.find(
          (el) => el.id === draggingSource.sourceId
        );
        if (sourcePoint && sourcePoint.type === "matching-point") {
          const sourceData = sourcePoint.data as {
            pointType?: "source" | "target";
            connections?: string[];
            allowMultipleConnections?: boolean;
          };
          if (sourceData.pointType === "source") {
            const currentConnections =
              matchingConnections[draggingSource.sourceId] || [];
            const allowMultiple = sourceData.allowMultipleConnections || false;

            if (!currentConnections.includes(snappedTarget)) {
              const updatedConnections = allowMultiple
                ? [...currentConnections, snappedTarget]
                : [snappedTarget];

              handleMatchingUpdate(draggingSource.sourceId, {
                connections: updatedConnections,
              });
            }
          }
        }
      }

      setDraggingSource(null);
      setMousePos(null);
      setSnappedTarget(null);
    };

    window.addEventListener("dragend", handleDragEnd);
    return () => {
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, [
    draggingSource,
    snappedTarget,
    elements,
    handleMatchingUpdate,
    matchingConnections,
  ]);

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // Allow matching point connections
      if (e.dataTransfer?.types.includes("application/x-matching-source-id")) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "link";

        // Mouse pozisyonunu güncelle (canvas'a göre)
        const canvasElement = e.currentTarget;
        if (canvasElement) {
          const rect = canvasElement.getBoundingClientRect();
          const mouseX = e.clientX - rect.left;
          const mouseY = e.clientY - rect.top;
          setMousePos({ x: mouseX, y: mouseY });

          // Mıknatıs efekti: Yakındaki target point'leri kontrol et
          const snapDistance = 50;
          const targetPoints = elements.filter(
            (el) =>
              el.type === "matching-point" &&
              (el.data as { pointType?: string })?.pointType === "target"
          );

          let closestTargetId: string | null = null;
          let closestDistance = Infinity;

          targetPoints.forEach((targetPoint) => {
            const targetCenterX =
              targetPoint.position.x + (targetPoint.size.width || 16) / 2;
            const targetCenterY =
              targetPoint.position.y + (targetPoint.size.height || 16) / 2;

            const dx = mouseX - targetCenterX;
            const dy = mouseY - targetCenterY;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= snapDistance && distance < closestDistance) {
              closestTargetId = targetPoint.id;
              closestDistance = distance;
            }
          });

          setSnappedTarget(closestTargetId);
        }
      }
    },
    [elements]
  );

  if (missingQuestion) {
    return (
      <div className="qe-preview-only">
        <div className="qe-preview-only__message">
          {t("preview.missingQuestion")}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="qe-preview-only">
        <div className="qe-preview-only__loader">{t("preview.loading")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="qe-preview-only">
        <div className="qe-preview-only__message">{error}</div>
      </div>
    );
  }

  const canvasWidth =
    typeof canvas.width === "number"
      ? canvas.width
      : parseNumber(canvas.width, DEFAULT_CANVAS_SETTINGS.width);
  const canvasHeight =
    typeof canvas.height === "number"
      ? canvas.height
      : parseNumber(canvas.height, DEFAULT_CANVAS_SETTINGS.height);

  // Matching connections SVG overlay için helper
  const matchingPoints = elements.filter((el) => el.type === "matching-point");
  const connections: Array<{
    sourceId: string;
    targetId: string;
    sourcePos: { x: number; y: number };
    targetPos: { x: number; y: number };
  }> = [];

  matchingPoints.forEach((sourcePoint) => {
    const sourceData = sourcePoint.data as {
      pointType?: "source" | "target";
      connections?: string[];
    };
    const connectionsData = matchingConnections[sourcePoint.id] || [];
    if (sourceData.pointType === "source" && connectionsData.length > 0) {
      connectionsData.forEach((targetId) => {
        const targetPoint = elements.find((el) => el.id === targetId);
        if (targetPoint) {
          const sourceCenterX =
            sourcePoint.position.x + (sourcePoint.size.width || 16) / 2;
          const sourceCenterY =
            sourcePoint.position.y + (sourcePoint.size.height || 16) / 2;
          const targetCenterX =
            targetPoint.position.x + (targetPoint.size.width || 16) / 2;
          const targetCenterY =
            targetPoint.position.y + (targetPoint.size.height || 16) / 2;
          connections.push({
            sourceId: sourcePoint.id,
            targetId,
            sourcePos: { x: sourceCenterX, y: sourceCenterY },
            targetPos: { x: targetCenterX, y: targetCenterY },
          });
        }
      });
    }
  });

  // Drag sırasında geçici çizgi ekle
  if (draggingSource && mousePos) {
    const sourcePoint = elements.find(
      (el) => el.id === draggingSource.sourceId
    );
    const sourcePos = sourcePoint
      ? {
          x: sourcePoint.position.x + (sourcePoint.size.width || 16) / 2,
          y: sourcePoint.position.y + (sourcePoint.size.height || 16) / 2,
        }
      : draggingSource.sourcePos;

    let targetPos = mousePos;
    if (snappedTarget) {
      const targetPoint = elements.find((el) => el.id === snappedTarget);
      if (targetPoint) {
        targetPos = {
          x: targetPoint.position.x + (targetPoint.size.width || 16) / 2,
          y: targetPoint.position.y + (targetPoint.size.height || 16) / 2,
        };
      }
    }

    connections.push({
      sourceId: draggingSource.sourceId,
      targetId: snappedTarget || "temp",
      sourcePos,
      targetPos,
    });
  }

  // Helper function to get appearance from source point
  const getSourceAppearance = (sourceId: string) => {
    const sourcePoint = matchingPoints.find((p) => p.id === sourceId);
    if (sourcePoint) {
      const data = sourcePoint.data as { appearance?: any };
      let connectionWidth = data.appearance?.connectionWidth;
      if (typeof connectionWidth === "string") {
        connectionWidth = parseFloat(connectionWidth) || 2;
      } else if (typeof connectionWidth === "number") {
        connectionWidth = connectionWidth || 2;
      } else {
        connectionWidth = 2;
      }
      return {
        connectionColor: data.appearance?.connectionColor || "#2563eb",
        connectionWidth,
        connectionStyle: data.appearance?.connectionStyle || "solid",
      };
    }
    return {
      connectionColor: "#2563eb",
      connectionWidth: 2,
      connectionStyle: "solid",
    };
  };

  return (
    <div className="qe-preview-only">
      <div className="qe-preview-only__canvas-wrapper">
        <div
          className="qe-preview-only__canvas"
          style={{
            width: canvasWidth,
            height: canvasHeight,
            backgroundColor: canvas.background,
            position: "relative",
            overflow: "hidden",
          }}
          onDragOver={handleDragOver}
        >
          {/* Matching connections SVG overlay */}
          {connections.length > 0 && (
            <svg
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 100,
                overflow: "visible",
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {connections.map((conn, index) => {
                const appearance = getSourceAppearance(conn.sourceId);
                const isTemporary =
                  conn.targetId === "temp" ||
                  (snappedTarget && conn.targetId === snappedTarget);

                if (isTemporary) {
                  const sourcePoint = matchingPoints.find(
                    (p) => p.id === conn.sourceId
                  );
                  const getPointRadius = (point: typeof sourcePoint) => {
                    if (!point) return 8;
                    const data = point.data as {
                      appearance?: { pointSize?: number | string };
                    };
                    const pointSize = data.appearance?.pointSize;
                    if (typeof pointSize === "number") {
                      return pointSize / 2;
                    } else if (typeof pointSize === "string") {
                      return parseFloat(pointSize) / 2 || 8;
                    }
                    return (point.size.width || 16) / 2;
                  };
                  const getBorderWidth = (point: typeof sourcePoint) => {
                    if (!point) return 0;
                    const data = point.data as {
                      appearance?: { borderWidth?: number | string };
                    };
                    const borderWidth = data.appearance?.borderWidth;
                    if (typeof borderWidth === "number") {
                      return borderWidth;
                    } else if (typeof borderWidth === "string") {
                      return parseFloat(borderWidth) || 0;
                    }
                    return 0;
                  };

                  const sourceRadius = getPointRadius(sourcePoint);
                  const sourceBorderWidth = getBorderWidth(sourcePoint);
                  const dx = conn.targetPos.x - conn.sourcePos.x;
                  const dy = conn.targetPos.y - conn.sourcePos.y;
                  const distance = Math.sqrt(dx * dx + dy * dy);
                  if (distance === 0) return null;
                  const unitX = dx / distance;
                  const unitY = dy / distance;

                  const startX =
                    conn.sourcePos.x +
                    unitX * (sourceRadius + sourceBorderWidth);
                  const startY =
                    conn.sourcePos.y +
                    unitY * (sourceRadius + sourceBorderWidth);

                  let endX = conn.targetPos.x;
                  let endY = conn.targetPos.y;
                  if (
                    conn.targetId !== "temp" &&
                    snappedTarget === conn.targetId
                  ) {
                    const targetPoint = matchingPoints.find(
                      (p) => p.id === conn.targetId
                    );
                    if (targetPoint) {
                      const targetRadius = getPointRadius(targetPoint);
                      const targetBorderWidth = getBorderWidth(targetPoint);
                      const targetDx = conn.targetPos.x - conn.sourcePos.x;
                      const targetDy = conn.targetPos.y - conn.sourcePos.y;
                      const targetDistance = Math.sqrt(
                        targetDx * targetDx + targetDy * targetDy
                      );
                      if (targetDistance > 0) {
                        const targetUnitX = targetDx / targetDistance;
                        const targetUnitY = targetDy / targetDistance;
                        endX =
                          conn.targetPos.x -
                          targetUnitX * (targetRadius + targetBorderWidth);
                        endY =
                          conn.targetPos.y -
                          targetUnitY * (targetRadius + targetBorderWidth);
                      }
                    }
                  }

                  // Smooth curve için path kullan
                  const curveDx = endX - startX;
                  const curveDy = endY - startY;
                  const controlX1 = startX + curveDx * 0.5;
                  const controlY1 = startY;
                  const controlX2 = endX - curveDx * 0.5;
                  const controlY2 = endY;

                  // connectionStyle'a göre stroke-dasharray değerini belirle
                  const width = appearance.connectionWidth || 2;
                  const dashArray =
                    appearance.connectionStyle === "dashed"
                      ? `${width * 4}, ${width * 2}`
                      : appearance.connectionStyle === "dotted"
                      ? `${width * 0.5}, ${width * 2}`
                      : "none";

                  return (
                    <g key={`temp-${conn.sourceId}-${index}`}>
                      <path
                        d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                        fill="none"
                        stroke={appearance.connectionColor}
                        strokeWidth={appearance.connectionWidth}
                        strokeLinecap="round"
                        strokeDasharray={dashArray}
                        style={{
                          pointerEvents: "none",
                        }}
                        opacity={0.5}
                      />
                    </g>
                  );
                }

                // Kalıcı bağlantılar için
                const sourcePoint = matchingPoints.find(
                  (p) => p.id === conn.sourceId
                );
                const targetPoint = matchingPoints.find(
                  (p) => p.id === conn.targetId
                );

                const getPointRadius = (point: typeof sourcePoint) => {
                  if (!point) return 8;
                  const data = point.data as {
                    appearance?: { pointSize?: number | string };
                  };
                  const pointSize = data.appearance?.pointSize;
                  if (typeof pointSize === "number") {
                    return pointSize / 2;
                  } else if (typeof pointSize === "string") {
                    return parseFloat(pointSize) / 2 || 8;
                  }
                  return (point.size.width || 16) / 2;
                };
                const getBorderWidth = (point: typeof sourcePoint) => {
                  if (!point) return 0;
                  const data = point.data as {
                    appearance?: { borderWidth?: number | string };
                  };
                  const borderWidth = data.appearance?.borderWidth;
                  if (typeof borderWidth === "number") {
                    return borderWidth;
                  } else if (typeof borderWidth === "string") {
                    return parseFloat(borderWidth) || 0;
                  }
                  return 0;
                };

                const sourceRadius = getPointRadius(sourcePoint);
                const sourceBorderWidth = getBorderWidth(sourcePoint);
                const targetRadius = getPointRadius(targetPoint);
                const targetBorderWidth = getBorderWidth(targetPoint);

                const dx = conn.targetPos.x - conn.sourcePos.x;
                const dy = conn.targetPos.y - conn.sourcePos.y;
                const distance = Math.sqrt(dx * dx + dy * dy);
                if (distance === 0) return null;
                const unitX = dx / distance;
                const unitY = dy / distance;

                const startX =
                  conn.sourcePos.x + unitX * (sourceRadius + sourceBorderWidth);
                const startY =
                  conn.sourcePos.y + unitY * (sourceRadius + sourceBorderWidth);
                const endX =
                  conn.targetPos.x - unitX * (targetRadius + targetBorderWidth);
                const endY =
                  conn.targetPos.y - unitY * (targetRadius + targetBorderWidth);

                // Smooth curve için path kullan
                const curveDx = endX - startX;
                const curveDy = endY - startY;
                const controlX1 = startX + curveDx * 0.5;
                const controlY1 = startY;
                const controlX2 = endX - curveDx * 0.5;
                const controlY2 = endY;

                // connectionStyle'a göre stroke-dasharray değerini belirle
                const width = appearance.connectionWidth || 2;
                const dashArray =
                  appearance.connectionStyle === "dashed"
                    ? `${width * 4}, ${width * 2}`
                    : appearance.connectionStyle === "dotted"
                    ? `${width * 0.5}, ${width * 2}`
                    : "none";

                // Bezier curve üzerinde t=0.5 noktasını hesapla (çizginin ortası)
                const t = 0.5;
                const oneMinusT = 1 - t;
                const midX =
                  oneMinusT * oneMinusT * oneMinusT * startX +
                  3 * oneMinusT * oneMinusT * t * controlX1 +
                  3 * oneMinusT * t * t * controlX2 +
                  t * t * t * endX;
                const midY =
                  oneMinusT * oneMinusT * oneMinusT * startY +
                  3 * oneMinusT * oneMinusT * t * controlY1 +
                  3 * oneMinusT * t * t * controlY2 +
                  t * t * t * endY;

                // Bağlantıyı kaldırma fonksiyonu
                const handleRemoveConnection = (e: React.MouseEvent) => {
                  e.stopPropagation();
                  const sourceElement = elements.find(
                    (el) => el.id === conn.sourceId
                  );
                  if (!sourceElement) return;

                  const sourceData = sourceElement.data as {
                    pointType?: "source" | "target";
                    connections?: string[];
                    allowMultipleConnections?: boolean;
                  };

                  const currentConnections =
                    matchingConnections[conn.sourceId] || [];
                  const updatedConnections = currentConnections.filter(
                    (id) => id !== conn.targetId
                  );

                  handleMatchingUpdate(conn.sourceId, {
                    data: {
                      ...sourceData,
                      connections: updatedConnections,
                    },
                  });
                };

                const connectionKey = `${conn.sourceId}-${conn.targetId}`;
                const isHovered = hoveredConnection === connectionKey;

                return (
                  <g key={`${conn.sourceId}-${conn.targetId}-${index}`}>
                    {/* Clickable path - clicking anywhere on the line removes the connection */}
                    <path
                      d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                      fill="none"
                      stroke={appearance.connectionColor}
                      strokeWidth={Math.max(appearance.connectionWidth + 8, 10)}
                      strokeLinecap="round"
                      strokeDasharray={dashArray}
                      onClick={handleRemoveConnection}
                      onMouseEnter={() => setHoveredConnection(connectionKey)}
                      onMouseLeave={() => setHoveredConnection(null)}
                      style={{
                        cursor: "pointer",
                        pointerEvents: "auto",
                        opacity: 0,
                      }}
                    />
                    {/* Visible path */}
                    <path
                      d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                      fill="none"
                      stroke={appearance.connectionColor}
                      strokeWidth={appearance.connectionWidth}
                      strokeLinecap="round"
                      strokeDasharray={dashArray}
                      style={{
                        filter: `drop-shadow(0 0 2px ${appearance.connectionColor}40)`,
                        pointerEvents: "none",
                      }}
                      opacity={isHovered ? 0.9 : 0.7}
                    />
                    {/* Gradient overlay for depth */}
                    <path
                      d={`M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`}
                      fill="none"
                      stroke={appearance.connectionColor}
                      strokeWidth={appearance.connectionWidth * 0.5}
                      strokeLinecap="round"
                      strokeDasharray={dashArray}
                      style={{
                        filter: `drop-shadow(0 0 2px ${appearance.connectionColor}40)`,
                        pointerEvents: "none",
                      }}
                      opacity={0.3}
                    />
                  </g>
                );
              })}
            </svg>
          )}

          {elements
            .filter((element) => !hiddenElements.has(element.id))
            .map((element) => (
              <div
                key={element.id}
                style={{
                  position: "absolute",
                  top: element.position.y,
                  left: element.position.x,
                  width: element.size.width,
                  height: element.size.height,
                  transform: element.rotation
                    ? `rotate(${element.rotation}deg)`
                    : undefined,
                  zIndex: element.zIndex,
                }}
              >
                {renderCanvasElementContent(element, {
                  mode: "preview",
                  formState: formState,
                  allElements: elements,
                  matchingConnections: matchingConnections,
                  onUpdate: handleElementUpdate,
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

interface EditorLayoutProps {
  onFormStateChange?: (formState: ElementFormState) => void;
}

const EditorLayout: React.FC<EditorLayoutProps> = ({ onFormStateChange }) => {
  const { t } = useLocale();
  const { assets, canvas, elements, selectedElementId, selectedElementIds, zoom, hiddenElements, dispatch } =
    useEditorState();
  const {
    addShortcut: addShortcutInternal,
    removeShortcut,
    getShortcutsForElement,
  } = useToolbarShortcuts();

  // Wrapper to add toast notification
  const addShortcut = useCallback(
    (shortcutConfig: any) => {
      const wasAdded = addShortcutInternal(shortcutConfig);
      if (wasAdded) {
        toast.success(`"${shortcutConfig.label}" ${t("toast.shortcutAdded")}`, {
          duration: 2000,
        });
      }
      // If not added, it means it already exists - no toast needed as it will be handled by remove
    },
    [addShortcutInternal]
  );

  const canvasRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const guideCounterRef = useRef(0);

  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [isLocalMediaDragActive, setLocalMediaDragActive] = useState(false);
  const [showCanvasRulers, setShowCanvasRulers] = useState(false);
  const [showCanvasGuides, setShowCanvasGuides] = useState(true);
  const [canvasGuides, setCanvasGuides] = useState<CanvasGuide[]>([]);
  const [activeCanvasGuidePreset, setActiveCanvasGuidePreset] =
    useState<CanvasGuidePreset | "">("");
  const [formState, setFormState] = useState<ElementFormState>({});
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isQuestionSettingsBarVisible, setQuestionSettingsBarVisible] =
    useState(true);
  const [isAnswerKeyOpen, setAnswerKeyOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isTemplatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [isTemplateNameModalOpen, setTemplateNameModalOpen] = useState(false);
  const [templateNameModalOptions, setTemplateNameModalOptions] = useState<{
    isDuplicate?: boolean;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    elementId: string;
    hasTextSelection?: boolean;
  } | null>(null);
  const [textToSpeechElementId, setTextToSpeechElementId] = useState<
    string | null
  >(null);
  const [isTextToSpeechSubmitting, setIsTextToSpeechSubmitting] =
    useState(false);
  const [mathTypeModalState, setMathTypeModalState] = useState<{
    isOpen: boolean;
    elementId: string | null;
  }>({ isOpen: false, elementId: null });
  const [isSaveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveSuccessModal, setSaveSuccessModal] = useState<{
    isOpen: boolean;
    message: string;
  }>({ isOpen: false, message: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [questionTypes, setQuestionTypes] = useState<QuestionType[]>([]);
  // Kullanıcının "hangi soru tipinde hangi form elemanları görünsün"
  // tercihi (localStorage'da kalıcı, soru tipi id'sine göre).
  const [formVisibility, setFormVisibility] = useState<FormElementVisibilityMap>(
    () => loadFormElementVisibility()
  );
  const [isFormVisibilityOpen, setIsFormVisibilityOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [definitionTags, setDefinitionTags] = useState<string[]>([]);
  const [isDefinitionsLoading, setIsDefinitionsLoading] = useState(true);
  const [hasResolvedQuestionId, setHasResolvedQuestionId] = useState(false);
  const [hasAttemptedQuestionLoad, setHasAttemptedQuestionLoad] =
    useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const [isQuestionLoaded, setIsQuestionLoaded] = useState(true);
  const [questionLoadError, setQuestionLoadError] = useState<string | null>(
    null
  );
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [persistedQuestionType, setPersistedQuestionType] = useState<
    "draft" | "template" | "question" | null
  >(null);
  const [pendingQuestionType, setPendingQuestionType] =
    useState<QuestionType | null>(null);

  // Selection box state for mouse selection
  const [selectionBox, setSelectionBox] = useState<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // Get current editor state for API calls
  const currentEditorState: EditorState = useMemo(
    () => ({
      canvas,
      elements,
      selectedElementId,
      selectedElementIds,
      zoom,
      assets,
      hiddenElements,
    }),
    [canvas, elements, selectedElementId, selectedElementIds, zoom, assets, hiddenElements]
  );

  // --- Dirty tracking: snapshot state after load/save to detect changes ---
  const lastSavedSnapshotRef = useRef<string | null>(null);

  const currentSnapshot = useMemo(() => {
    return JSON.stringify({ canvas, elements, assets });
  }, [canvas, elements, assets]);

  const hasUnsavedChanges = useMemo(() => {
    if (lastSavedSnapshotRef.current === null) return false;
    return currentSnapshot !== lastSavedSnapshotRef.current;
  }, [currentSnapshot]);

  // Initialize hidden elements when elements change (when video elements have showElementsAfterVideo configured)
  useEffect(() => {
    dispatch({ type: "initialize-hidden-elements" });
  }, [elements, dispatch]);

  // Listen for show-elements-after-video event from VideoElement
  useEffect(() => {
    const handleShowElementsAfterVideo = (event: CustomEvent) => {
      const { elementIds } = event.detail;
      if (elementIds && Array.isArray(elementIds)) {
        dispatch({ type: "show-elements", elementIds });
      }
    };

    window.addEventListener("show-elements-after-video", handleShowElementsAfterVideo as EventListener);
    return () => {
      window.removeEventListener("show-elements-after-video", handleShowElementsAfterVideo as EventListener);
    };
  }, [dispatch]);

  const syncQuestionIdFromResponse = useCallback(
    (response: SaveQuestionResponse) => {
      if (!response.success || !response.questionId) {
        return;
      }

      setActiveQuestionId((prev) => {
        const nextId = String(response.questionId);
        if (prev === nextId) {
          return prev;
        }

        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          params.set("questionId", nextId);
          const nextUrl = `${window.location.pathname}?${params.toString()}`;
          window.history.replaceState({}, "", nextUrl);
        }

        return nextId;
      });
    },
    []
  );

  const redirectToQuestions = useCallback(() => {
    if (typeof window !== "undefined") {
      const shouldRedirectToAdmin =
        process.env.REACT_APP_REDIRECT_TO_ADMIN === "true" ||
        process.env.NODE_ENV === "production";
      if (!shouldRedirectToAdmin) {
        return;
      }
      setIsRedirecting(true);
      window.location.href =
        process.env.REACT_APP_ADMIN_URL ||
        "https://admin.yougo.com.tr/backend/questions";
    }
  }, []);

  const fetchDefinitions = useCallback(async () => {
    try {
      setIsDefinitionsLoading(true);
      const response = await apiService.getDefinitions();
      setLessons(response.data.lessons || []);
      setGrades(response.data.grades || []);
      setSubjects(response.data.subjects || []);
      setQuestionTypes(response.data.questionTypes || []);
      setTemplates(response.data.templates || []);
      setDefinitionTags(response.data.tags || []);
    } catch (error) {
      if ((error as { status?: number } | null)?.status === 401) {
        redirectToQuestions();
        return;
      }
      // Ignore, UI already shows empty states where needed
    } finally {
      setIsDefinitionsLoading(false);
    }
  }, [redirectToQuestions]);

  // Fetch definitions on mount
  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  // Parse questionId from URL once on mount
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get("questionId");
    const previewParam = params.get("previewMode");

    setActiveQuestionId(idParam);
    if (!idParam) {
      setPersistedQuestionType(null);
    }

    if (previewParam && previewParam.toLowerCase() === "active") {
      setIsPreviewMode(true);
    }

    setHasResolvedQuestionId(true);
  }, []);

  useEffect(() => {
    setHasAttemptedQuestionLoad(true);
    if (!activeQuestionId) {
      setIsQuestionLoaded(true);
      setQuestionLoadError(null);
      setPersistedQuestionType(null);
      return;
    }

    let isMounted = true;

    const loadQuestion = async () => {
      try {
        setIsQuestionLoaded(false);
        setQuestionLoadError(null);
        setIsQuestionLoading(true);
        const response = await apiService.getQuestion(activeQuestionId);
        const questionData = (response as any)?.data?.question ?? response;
        const responseData = (response as any)?.data ?? response;

        // Check if answerKey is at the top level (outside of question object)
        const topLevelAnswerKey = responseData?.answerKey;

        // Check if solutionVideoUrl, topicVideoUrl, solutionTextUrl are at the top level
        const topLevelSolutionVideoUrl = responseData?.solutionVideoUrl;
        const topLevelTopicVideoUrl = responseData?.topicVideoUrl;
        const topLevelSolutionTextUrl = responseData?.solutionTextUrl;

        // Merge top-level fields into question data if they exist
        const payload = {
          ...questionData,
          ...(topLevelAnswerKey && { answerKey: topLevelAnswerKey }),
          ...(topLevelSolutionVideoUrl && {
            solutionVideoUrl: topLevelSolutionVideoUrl,
          }),
          ...(topLevelTopicVideoUrl && {
            topicVideoUrl: topLevelTopicVideoUrl,
          }),
          ...(topLevelSolutionTextUrl && {
            solutionTextUrl: topLevelSolutionTextUrl,
          }),
        };

        if (!payload || !isMounted) {
          return;
        }

        const normalized = normalizeQuestionPayload(payload);
        dispatch({
          type: "set-state",
          state: normalized,
        });

        // Capture snapshot for dirty tracking
        lastSavedSnapshotRef.current = JSON.stringify({
          canvas: normalized.canvas,
          elements: normalized.elements,
          assets: normalized.assets,
        });

        const responseId = (response as any)?.data?.id;
        if (responseId && String(responseId) !== String(activeQuestionId)) {
          setActiveQuestionId(String(responseId));
        }

        const loadedTypeRaw = payload?.type;
        if (
          loadedTypeRaw === "draft" ||
          loadedTypeRaw === "template" ||
          loadedTypeRaw === "question"
        ) {
          setPersistedQuestionType(loadedTypeRaw);
        } else {
          setPersistedQuestionType(null);
        }

        setIsQuestionLoaded(true);
        setQuestionLoadError(null);
        if (!isPreviewMode) {
          toast.success(t("toast.questionLoaded"));
        }
      } catch (error) {
        if ((error as { status?: number } | null)?.status === 401) {
          redirectToQuestions();
          return;
        }
        const fallbackMessage = t("toast.questionLoadFailed");
        setQuestionLoadError(fallbackMessage);
        setIsQuestionLoaded(true);
        setPersistedQuestionType(null);
        if (!isPreviewMode) {
          toast.error(fallbackMessage);
        }
      } finally {
        if (isMounted) {
          setIsQuestionLoading(false);
        }
      }
    };

    loadQuestion();

    return () => {
      isMounted = false;
    };
  }, [activeQuestionId, dispatch, isPreviewMode, redirectToQuestions]);

  useEffect(() => {
    if (isInitialLoadComplete) {
      return;
    }
    if (!hasResolvedQuestionId) {
      return;
    }
    if (isDefinitionsLoading) {
      return;
    }
    if (!hasAttemptedQuestionLoad) {
      return;
    }
    if (isQuestionLoading || !isQuestionLoaded) {
      return;
    }
    setIsInitialLoadComplete(true);
  }, [
    hasAttemptedQuestionLoad,
    hasResolvedQuestionId,
    isDefinitionsLoading,
    isInitialLoadComplete,
    isQuestionLoading,
    isQuestionLoaded,
  ]);

  const stageStyle = useMemo<React.CSSProperties>(() => {
    const base: React.CSSProperties = {
      backgroundColor: "var(--qe-bg-surface)",
    };

    if (!canvas.grid.enabled) {
      return { ...base, backgroundImage: "none" };
    }

    const gridSize = Math.max(4, Math.min(canvas.grid.size || 0, 240));
    const gridColor = "rgba(37, 99, 235, 0.05)";

    return {
      ...base,
      backgroundImage: [
        `repeating-linear-gradient(0deg, ${gridColor}, ${gridColor} 1px, transparent 1px, transparent ${gridSize}px)`,
        `repeating-linear-gradient(90deg, ${gridColor}, ${gridColor} 1px, transparent 1px, transparent ${gridSize}px)`,
      ].join(","),
    };
  }, [canvas.grid.enabled, canvas.grid.size]);

  const hiddenAreaSize = Math.max(0, canvas.hiddenArea.size || 0);
  const shouldRenderHiddenArea =
    canvas.hiddenArea.enabled && hiddenAreaSize > 0;

  // Learning outcomes is optional - removed shouldRequireOutcome check
  const isQuestionSettingsComplete = useMemo(() => {
    const question = canvas.question;
    if (!question) {
      return false;
    }

    const required = [
      question.questionType?.id,
      question.grade,
      question.lesson,
      question.subject,
    ];

    // Learning outcomes is optional - not required for normal save

    return required.every((value) =>
      typeof value === "string" ? value.trim().length > 0 : Boolean(value)
    );
  }, [canvas.question]);

  // Check if there are any interactive form elements
  const hasInteractiveElements = useMemo(() => {
    const interactiveTypes = [
      "choice",
      "drop-target",
      "order-list",
      "matching-point",
      "fill-in-the-blanks",
      "paint-area",
      "push-to-talk",
    ];
    return elements.some((el) => interactiveTypes.includes(el.type));
  }, [elements]);

  // Check if answer key is valid (if interactive elements exist)
  const hasValidAnswerKey = useMemo(() => {
    if (!hasInteractiveElements) {
      return false; // No interactive elements means no answer key needed, but we need at least one element
    }
    const answerKeyValidation = validateAnswerKey(
      elements,
      canvas.question?.answerKey
    );
    return answerKeyValidation.isValid;
  }, [elements, canvas.question?.answerKey, hasInteractiveElements]);

  const normalSaveDisabled =
    !isQuestionSettingsComplete ||
    isQuestionLoading ||
    !hasInteractiveElements ||
    !hasValidAnswerKey;
  const hasExistingQuestion = Boolean(activeQuestionId);

  // Calculate dynamic canvas size based on elements
  // Default: 900x600, Max: 2700x2400
  const dynamicCanvasSize = useMemo(() => {
    const MIN_HEIGHT = DEFAULT_EDITOR_CANVAS_HEIGHT;
    const MAX_HEIGHT = MAX_EDITOR_CANVAS_HEIGHT;

    // Expandable kapalıysa sabit boyutu kullan
    if (canvas.expandable === false) {
      return {
        width: canvas.width,
        height: canvas.height,
      };
    }

    // Genişlik her zaman sabit
    const fixedWidth = canvas.width;

    // Elementler yoksa minimum yüksekliğe dön
    if (elements.length === 0) {
      return {
        width: fixedWidth,
        height: MIN_HEIGHT,
      };
    }

    const innerPaddingTop = canvas.innerPadding?.top || 0;
    const innerPaddingBottom = canvas.innerPadding?.bottom || 0;

    let maxY = 0;

    elements.forEach((element) => {
      const { width, height } = resolveElementSizeForBounds(element);
      const { bottom } = getRotatedVisualBounds(
        element.position,
        { width, height },
        element.rotation
      );

      const adjustedBottom = bottom + innerPaddingTop;
      maxY = Math.max(maxY, adjustedBottom);
    });

    const elementPadding = 20;
    const totalRequiredHeight = maxY + elementPadding + innerPaddingBottom;

    // Sadece dikey genişleme: yüksekliği elementlere göre hesapla
    let calculatedHeight = Math.max(MIN_HEIGHT, totalRequiredHeight);
    calculatedHeight = Math.min(MAX_HEIGHT, calculatedHeight);

    return {
      width: fixedWidth,
      height: Math.round(calculatedHeight),
    };
  }, [
    elements,
    canvas.expandable,
    canvas.width,
    canvas.height,
    canvas.innerPadding,
  ]);

  const addCanvasGuide = useCallback(
    (orientation: CanvasGuideOrientation, position: number) => {
      guideCounterRef.current += 1;
      const nextGuide: CanvasGuide = {
        id: `canvas-guide-${guideCounterRef.current}`,
        orientation,
        position: clampCanvasGuidePosition(
          position,
          orientation,
          dynamicCanvasSize
        ),
      };

      setCanvasGuides((current) => [...current, nextGuide]);
      setActiveCanvasGuidePreset("");
      setShowCanvasGuides(true);
    },
    [dynamicCanvasSize]
  );

  const replaceCanvasGuides = useCallback(
    (
      presets: Array<{
        orientation: CanvasGuideOrientation;
        position: number;
      }>
    ) => {
      const nextGuides = presets.map((preset) => {
        guideCounterRef.current += 1;
        return {
          id: `canvas-guide-${guideCounterRef.current}`,
          orientation: preset.orientation,
          position: clampCanvasGuidePosition(
            preset.position,
            preset.orientation,
            dynamicCanvasSize
          ),
        };
      });

      setCanvasGuides(nextGuides);
      setShowCanvasGuides(true);
      setShowCanvasRulers(true);
    },
    [dynamicCanvasSize]
  );

  const applyCanvasGuidePreset = useCallback(
    (preset: CanvasGuidePreset) => {
      setActiveCanvasGuidePreset(preset);
      switch (preset) {
        case "margin-50":
          replaceCanvasGuides([
            { orientation: "vertical", position: 50 },
            { orientation: "vertical", position: dynamicCanvasSize.width - 50 },
            { orientation: "horizontal", position: 50 },
            {
              orientation: "horizontal",
              position: dynamicCanvasSize.height - 50,
            },
          ]);
          return;
        case "center":
          replaceCanvasGuides([
            {
              orientation: "vertical",
              position: dynamicCanvasSize.width / 2,
            },
            {
              orientation: "horizontal",
              position: dynamicCanvasSize.height / 2,
            },
          ]);
          return;
        case "columns-3":
          replaceCanvasGuides([
            {
              orientation: "vertical",
              position: dynamicCanvasSize.width / 3,
            },
            {
              orientation: "vertical",
              position: (dynamicCanvasSize.width * 2) / 3,
            },
          ]);
          return;
        case "rows-3":
          replaceCanvasGuides([
            {
              orientation: "horizontal",
              position: dynamicCanvasSize.height / 3,
            },
            {
              orientation: "horizontal",
              position: (dynamicCanvasSize.height * 2) / 3,
            },
          ]);
      }
    },
    [dynamicCanvasSize, replaceCanvasGuides]
  );

  const updateCanvasGuide = useCallback(
    (guideId: string, position: number) => {
      setCanvasGuides((current) =>
        current.map((guide) =>
          guide.id === guideId
            ? {
                ...guide,
                position: clampCanvasGuidePosition(
                  position,
                  guide.orientation,
                  dynamicCanvasSize
                ),
              }
            : guide
        )
      );
      setActiveCanvasGuidePreset("");
    },
    [dynamicCanvasSize]
  );

  const removeCanvasGuide = useCallback((guideId: string) => {
    setCanvasGuides((current) =>
      current.filter((guide) => guide.id !== guideId)
    );
    setActiveCanvasGuidePreset("");
  }, []);

  const getCanvasGuidePosition = useCallback(
    (orientation: CanvasGuideOrientation, clientX: number, clientY: number) => {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) {
        return 0;
      }

      return orientation === "vertical"
        ? (clientX - canvasRect.left) / zoom
        : (clientY - canvasRect.top) / zoom;
    },
    [zoom]
  );

  const handleCanvasRulerPointerDown = useCallback(
    (
      orientation: CanvasGuideOrientation,
      event: React.PointerEvent<HTMLButtonElement>
    ) => {
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      addCanvasGuide(
        orientation,
        getCanvasGuidePosition(orientation, event.clientX, event.clientY)
      );
    },
    [addCanvasGuide, getCanvasGuidePosition]
  );

  // Canvas yüksekliğini elementlere göre otomatik ayarla (sadece expandable modda)
  // Genişlik sabit kalır, sadece yükseklik değişir
  useEffect(() => {
    if (canvas.expandable === false) {
      return;
    }

    dispatch({
      type: "update-canvas",
      origin: "system",
      changes: {
        height: dynamicCanvasSize.height,
      },
    });
  }, [
    dynamicCanvasSize.height,
    canvas.expandable,
    dispatch,
  ]);

  const selectedElement = useMemo(
    () => elements.find((element) => element.id === selectedElementId) || null,
    [elements, selectedElementId]
  );

  const activeQuestionTypeId = canvas.question?.questionType?.id
    ? String(canvas.question.questionType.id)
    : "";

  const activeQuestionType = useMemo<QuestionType | null>(() => {
    if (activeQuestionTypeId) {
      const fromList = questionTypes.find(
        (type) => String(type.id) === activeQuestionTypeId
      );
      if (fromList) {
        return fromList;
      }
    }

    if (canvas.question?.questionType?.name) {
      return {
        id: canvas.question.questionType.id,
        name: canvas.question.questionType.name,
        displayName: canvas.question.questionType.name,
      };
    }

    return null;
  }, [activeQuestionTypeId, questionTypes, canvas.question?.questionType]);

  // Varsayılan (kod tanımlı) filtre — kullanıcı override'ı yoksa geçerli.
  const resolveDefaultAllowedTemplates = useCallback(
    (
      reference: { id?: string; name?: string; displayName?: string | null }
    ): ElementTemplateKey[] | null => {
      const matchedKey =
        findQuestionTypeFilterKey(reference.id) ??
        findQuestionTypeFilterKey(reference.displayName || reference.name);
      return matchedKey ? getQuestionTypeLibraryFilters()[matchedKey] : null;
    },
    []
  );

  const allowedTemplates = useMemo<ElementTemplateKey[] | null>(() => {
    const questionTypeReference =
      activeQuestionType || canvas.question?.questionType;
    if (!questionTypeReference) {
      return null;
    }

    // Önce kullanıcı override'ı (soru tipi id'sine göre) — varsa onu kullan.
    const overrideKey = String(questionTypeReference.id ?? "");
    if (overrideKey && formVisibility[overrideKey]) {
      return formVisibility[overrideKey];
    }

    return resolveDefaultAllowedTemplates(
      questionTypeReference as QuestionType
    );
  }, [
    activeQuestionType,
    canvas.question?.questionType,
    formVisibility,
    resolveDefaultAllowedTemplates,
  ]);

  const hasActiveQuestionType = Boolean(
    activeQuestionTypeId || activeQuestionType
  );

  const allowedTemplateSet = useMemo(() => {
    // null => filtre yok (hepsini göster). Boş dizi => kullanıcı hepsini
    // kapatmış (sadece Temel Öğeler görünür).
    if (allowedTemplates === null) {
      return null;
    }
    return new Set<ElementTemplateKey>(allowedTemplates);
  }, [allowedTemplates]);

  // Ayarlanabilir form elemanları (Temel Öğeler her zaman görünür, filtreye
  // tabi değil — bu yüzden yalnızca formElements bölümü listelenir).
  const configurableFormElementItems = useMemo(() => {
    const section = getElementLibrarySections().find(
      (s) => s.id === "formElements"
    );
    return (section?.items ?? [])
      .filter((item) => Boolean(item.template))
      .map((item) => ({
        template: item.template as ElementTemplateKey,
        label: item.label,
      }));
  }, []);

  const handleSaveFormVisibility = useCallback(
    (next: FormElementVisibilityMap) => {
      setFormVisibility(next);
      saveFormElementVisibility(next);
      setIsFormVisibilityOpen(false);
    },
    []
  );

  const isMatchingQuestionType = useMemo(() => {
    if (!activeQuestionType && !canvas.question?.questionType) {
      return false;
    }
    const questionTypeReference =
      activeQuestionType || canvas.question?.questionType;
    const matchedKey =
      findQuestionTypeFilterKey(questionTypeReference?.id) ??
      findQuestionTypeFilterKey(
        (questionTypeReference as QuestionType)?.displayName ||
          questionTypeReference?.name
      );
    return matchedKey === "matching" || matchedKey === "eslestirme";
  }, [activeQuestionType, canvas.question?.questionType]);

  const filteredLibrarySections = useMemo(() => {
    const sections = getElementLibrarySections();

    if (!hasActiveQuestionType) {
      return sections.filter((section) => section.id === "basic");
    }

    if (!allowedTemplateSet) {
      return sections;
    }

    return sections.map((section) => {
      if (section.id === "basic") {
        return section;
      }

      const items = section.items.filter((item) => {
        if (!item.template) {
          return true;
        }
        // Matching point / eşleştirme çiftleri sadece matching soru
        // tipinde görünsün
        if (
          item.template === "matching-point-source-basic" ||
          item.template === "matching-point-target-basic" ||
          item.template === "matching-2-pairs" ||
          item.template === "matching-3-pairs" ||
          item.template === "matching-4-pairs"
        ) {
          return (
            isMatchingQuestionType && allowedTemplateSet.has(item.template)
          );
        }
        return allowedTemplateSet.has(item.template);
      });

      return { ...section, items };
    }).filter((section) => {
      if (section.id === "basic") {
        return true;
      }
      return section.items.length > 0;
    });
  }, [allowedTemplateSet, hasActiveQuestionType, isMatchingQuestionType]);

  const activeQuestionTypeName = useMemo(() => {
    return (
      activeQuestionType?.displayName ||
      activeQuestionType?.name ||
      canvas.question?.questionType?.name ||
      ""
    );
  }, [activeQuestionType, canvas.question?.questionType?.name]);

  const isQuestionTypesLoading =
    isDefinitionsLoading && questionTypes.length === 0;

  const isActiveQuestionTypeInList = useMemo(
    () =>
      Boolean(
        activeQuestionTypeId &&
          questionTypes.some((type) => String(type.id) === activeQuestionTypeId)
      ),
    [questionTypes, activeQuestionTypeId]
  );

  const defaultQuestionTypeId = useMemo(() => {
    if (questionTypes.length === 0) {
      return "";
    }

    return String(questionTypes[0].id);
  }, [questionTypes]);

  const selectQuestionTypeValue = isActiveQuestionTypeInList
    ? activeQuestionTypeId
    : defaultQuestionTypeId;

  const handleSelectElement = useCallback(
    (elementId: string | null, event?: React.PointerEvent) => {
      if (elementId === null) {
        dispatch({ type: "select-elements", elementIds: [] });
        return;
      }

      // Check for modifier keys (Shift or Ctrl/Cmd)
      const isMultiSelect =
        event && (event.shiftKey || event.ctrlKey || event.metaKey);

      if (isMultiSelect) {
        // Toggle selection
        dispatch({ type: "toggle-element-selection", elementId });
      } else {
        // Single selection
        dispatch({ type: "select-elements", elementIds: [elementId] });
      }
    },
    [dispatch]
  );

  const handleSelectQuestionType = useCallback(
    (type: QuestionType) => {
      if (elements.length > 0) {
        setPendingQuestionType(type);
        return;
      }

      dispatch({
        type: "update-canvas",
        changes: {
          question: {
            ...(canvas.question || {}),
            questionType: {
              id: String(type.id),
              name: type.displayName || type.name || "",
            },
          },
        },
      });
    },
    [dispatch, canvas.question, elements.length]
  );

  const handleConfirmQuestionTypeChange = useCallback(() => {
    if (!pendingQuestionType) {
      return;
    }

    dispatch({
      type: "set-state",
      state: {
        canvas: {
          ...canvas,
          question: {
            ...(canvas.question || {}),
            questionType: {
              id: String(pendingQuestionType.id),
              name:
                pendingQuestionType.displayName ||
                pendingQuestionType.name ||
                "",
            },
            answerKey: undefined,
          },
        },
        elements: [],
        assets: assets,
      },
    });

    setPendingQuestionType(null);
  }, [pendingQuestionType, canvas, dispatch, assets]);

  const handleCancelQuestionTypeChange = useCallback(() => {
    setPendingQuestionType(null);
  }, []);

  useEffect(() => {
    if (hasActiveQuestionType) {
      return;
    }

    if (questionTypes.length === 0) {
      return;
    }

    handleSelectQuestionType(questionTypes[0]);
  }, [handleSelectQuestionType, hasActiveQuestionType, questionTypes]);

  // Global mouse event handlers for selection box
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isSelecting && selectionBox && canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const innerPaddingLeft = canvas.innerPadding?.left || 0;
        const innerPaddingTop = canvas.innerPadding?.top || 0;
        const endX = (e.clientX - canvasRect.left) / zoom - innerPaddingLeft;
        const endY = (e.clientY - canvasRect.top) / zoom - innerPaddingTop;
        setSelectionBox({ ...selectionBox, endX, endY });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isSelecting && selectionBox && canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const innerPaddingLeft = canvas.innerPadding?.left || 0;
        const innerPaddingTop = canvas.innerPadding?.top || 0;
        const endX = (e.clientX - canvasRect.left) / zoom - innerPaddingLeft;
        const endY = (e.clientY - canvasRect.top) / zoom - innerPaddingTop;

        // Calculate selection box bounds
        const minX = Math.min(selectionBox.startX, endX);
        const maxX = Math.max(selectionBox.startX, endX);
        const minY = Math.min(selectionBox.startY, endY);
        const maxY = Math.max(selectionBox.startY, endY);

        // Find elements that intersect with selection box
        const selectedIds: string[] = [];
        elements.forEach((element) => {
          const elementLeft = element.position.x;
          const elementTop = element.position.y;
          const elementRight = elementLeft + (element.size.width || 0);
          const elementBottom =
            elementTop + (element.size.height || element.size.width || 0);

          // Check if element intersects with selection box
          if (
            elementLeft < maxX &&
            elementRight > minX &&
            elementTop < maxY &&
            elementBottom > minY
          ) {
            selectedIds.push(element.id);
          }
        });

        // Select found elements
        if (selectedIds.length > 0) {
          dispatch({ type: "select-elements", elementIds: selectedIds });
        } else {
          handleSelectElement(null);
        }

        setIsSelecting(false);
        setSelectionBox(null);
      }
    };

    if (isSelecting) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [
    isSelecting,
    selectionBox,
    canvas,
    zoom,
    elements,
    dispatch,
    handleSelectElement,
  ]);

  const handleUpdateElementData = useCallback(
    (elementId: string, data: Record<string, unknown>) => {
      console.log("handleUpdateElementData called:", elementId, data);
      dispatch({ type: "update-element", elementId, changes: { data } });
    },
    [dispatch]
  );

  // Store initial positions for multi-select drag
  const multiSelectStartPositionsRef = useRef<
    Map<string, { x: number; y: number }>
  >(new Map());
  const dragHistoryRef = useRef<{ active: boolean; started: boolean }>({
    active: false,
    started: false,
  });

  const handleDragStart = useCallback(
    (elementId: string, position: { x: number; y: number }) => {
      console.log("Drag start:", { elementId, selectedElementIds, position });
      dragHistoryRef.current = { active: true, started: false };
      // Store initial positions for all selected elements when drag starts
      if (
        selectedElementIds &&
        selectedElementIds.length > 1 &&
        selectedElementIds.includes(elementId)
      ) {
        selectedElementIds.forEach((id) => {
          const el = elements.find((e) => e.id === id);
          if (el) {
            multiSelectStartPositionsRef.current.set(id, { ...el.position });
          }
        });
        console.log(
          "Multi-select drag start - stored positions:",
          Array.from(multiSelectStartPositionsRef.current.entries())
        );
      } else {
        // Single element - store its position
        multiSelectStartPositionsRef.current.set(elementId, position);
      }
    },
    [selectedElementIds, elements]
  );

  const snapPositionToCanvasGuides = useCallback(
    (elementId: string, position: { x: number; y: number }) => {
      if (!showCanvasGuides || canvasGuides.length === 0) {
        return position;
      }

      const element = elements.find((candidate) => candidate.id === elementId);
      if (!element) {
        return position;
      }

      const width = element.size.width || 0;
      const height = element.size.height || element.size.width || 0;
      const threshold = 6 / Math.max(zoom, 0.1);
      let nextX = position.x;
      let nextY = position.y;
      let closestXDistance = threshold + 1;
      let closestYDistance = threshold + 1;

      canvasGuides.forEach((guide) => {
        if (guide.orientation === "vertical") {
          [position.x, position.x + width / 2, position.x + width].forEach(
            (edge, edgeIndex) => {
              const distance = Math.abs(edge - guide.position);
              if (distance <= threshold && distance < closestXDistance) {
                closestXDistance = distance;
                nextX =
                  guide.position -
                  (edgeIndex === 1 ? width / 2 : edgeIndex === 2 ? width : 0);
              }
            }
          );
          return;
        }

        [position.y, position.y + height / 2, position.y + height].forEach(
          (edge, edgeIndex) => {
            const distance = Math.abs(edge - guide.position);
            if (distance <= threshold && distance < closestYDistance) {
              closestYDistance = distance;
              nextY =
                guide.position -
                (edgeIndex === 1 ? height / 2 : edgeIndex === 2 ? height : 0);
            }
          }
        );
      });

      return { x: nextX, y: nextY };
    },
    [canvasGuides, elements, showCanvasGuides, zoom]
  );

  const handleMoveElement = useCallback(
    (elementId: string, position: { x: number; y: number }) => {
      position = snapPositionToCanvasGuides(elementId, position);

      if (dragHistoryRef.current.active && !dragHistoryRef.current.started) {
        dispatch({ type: "begin-gesture", gesture: "move" });
        dragHistoryRef.current.started = true;
      }
      // Check if multiple elements are selected and this element is one of them
      if (
        selectedElementIds &&
        selectedElementIds.length > 1 &&
        selectedElementIds.includes(elementId)
      ) {
        console.log("Multi-select drag:", {
          elementId,
          selectedElementIds,
          position,
          initialPositions: Array.from(
            multiSelectStartPositionsRef.current.entries()
          ),
        });
        // Get initial position (stored when drag started)
        const initialPos = multiSelectStartPositionsRef.current.get(elementId);
        if (!initialPos) {
          console.log("No initial position found, storing now");
          // Fallback: store current position and use it as initial
          const element = elements.find((el) => el.id === elementId);
          if (!element) return;
          multiSelectStartPositionsRef.current.set(elementId, {
            ...element.position,
          });
          // Also store other selected elements' positions
          selectedElementIds.forEach((id) => {
            const el = elements.find((e) => e.id === id);
            if (el && !multiSelectStartPositionsRef.current.has(id)) {
              multiSelectStartPositionsRef.current.set(id, { ...el.position });
            }
          });
          // Calculate delta from stored initial position
          const storedInitialPos =
            multiSelectStartPositionsRef.current.get(elementId);
          if (storedInitialPos) {
            const delta = {
              x: position.x - storedInitialPos.x,
              y: position.y - storedInitialPos.y,
            };
            // Create a Map with initial positions for all selected elements
            const initialPositionsMap = new Map<
              string,
              { x: number; y: number }
            >();
            selectedElementIds.forEach((id) => {
              const storedPos = multiSelectStartPositionsRef.current.get(id);
              if (storedPos) {
                initialPositionsMap.set(id, storedPos);
              } else {
                // Fallback: use current position
                const el = elements.find((e) => e.id === id);
                if (el) {
                  initialPositionsMap.set(id, { ...el.position });
                }
              }
            });
            dispatch({
              type: "move-elements",
              elementIds: selectedElementIds,
              delta,
              initialPositions: initialPositionsMap,
            });
          } else {
            dispatch({ type: "move-element", elementId, position });
          }
          return;
        }

        // Calculate delta from initial position (always use the original start position)
        const rawDelta = {
          x: position.x - initialPos.x,
          y: position.y - initialPos.y,
        };

        // Constrain delta based on canvas boundaries for ALL selected elements
        // This ensures no element goes outside canvas bounds
        const innerPaddingLeft = canvas.innerPadding?.left || 0;
        const innerPaddingTop = canvas.innerPadding?.top || 0;
        const innerPaddingRight = canvas.innerPadding?.right || 0;
        const innerPaddingBottom = canvas.innerPadding?.bottom || 0;
        const maxCanvasWidth = dynamicCanvasSize.width;
        const maxCanvasHeight = dynamicCanvasSize.height;
        const canvasExpandable = canvas.expandable !== false;
        const MAX_HEIGHT = MAX_EDITOR_CANVAS_HEIGHT;
        const isAtMaxHeight = maxCanvasHeight >= MAX_HEIGHT;
        // Genişlik sabit olduğu için sağ sınır her zaman uygulanır
        const enforceRightBoundary = true;
        // Enforce bottom boundary if: canvas is not expandable or max height reached
        const enforceBottomBoundary = !canvasExpandable || isAtMaxHeight;

        // Calculate constraints for each selected element
        // Find the most restrictive delta values that keep all elements within bounds
        let minDeltaX = -Infinity; // Minimum delta.x (for left/top boundaries)
        let maxDeltaX = Infinity; // Maximum delta.x (for right boundary)
        let minDeltaY = -Infinity; // Minimum delta.y (for left/top boundaries)
        let maxDeltaY = Infinity; // Maximum delta.y (for bottom boundary)

        selectedElementIds.forEach((id) => {
          const element = elements.find((el) => el.id === id);
          if (!element) return;

          const initialElementPos =
            multiSelectStartPositionsRef.current.get(id);
          if (!initialElementPos) return;

          const elementWidth = element.size.width;
          const elementHeight = element.size.height || 40;
          const rotation = element.rotation || 0;
          const rad = (rotation * Math.PI) / 180;
          const absCos = Math.abs(Math.cos(rad));
          const absSin = Math.abs(Math.sin(rad));

          // Calculate Axis-Aligned Bounding Box (AABB) dimensions for rotated element
          const rotatedWidth = elementWidth * absCos + elementHeight * absSin;
          const rotatedHeight = elementWidth * absSin + elementHeight * absCos;

          // Calculate visual offset due to rotation
          const offsetX = (elementWidth - rotatedWidth) / 2;
          const offsetY = (elementHeight - rotatedHeight) / 2;

          // Calculate visual bounds at initial position
          const initialVisualMinX = initialElementPos.x + offsetX;
          const initialVisualMaxX =
            initialElementPos.x + elementWidth - offsetX;
          const initialVisualMinY = initialElementPos.y + offsetY;
          const initialVisualMaxY =
            initialElementPos.y + elementHeight - offsetY;

          // Calculate required delta to keep element within left boundary
          // visualMinX + deltaX >= innerPaddingLeft
          // deltaX >= innerPaddingLeft - visualMinX
          const minDeltaXForLeft = innerPaddingLeft - initialVisualMinX;
          minDeltaX = Math.max(minDeltaX, minDeltaXForLeft);

          // Calculate required delta to keep element within top boundary
          // visualMinY + deltaY >= innerPaddingTop
          // deltaY >= innerPaddingTop - visualMinY
          const minDeltaYForTop = innerPaddingTop - initialVisualMinY;
          minDeltaY = Math.max(minDeltaY, minDeltaYForTop);

          // Check right boundary (enforce if canvas is not expandable, width is locked, or max width reached)
          if (enforceRightBoundary) {
            const maxVisualX = maxCanvasWidth - innerPaddingRight;
            // Calculate required delta to keep element within right boundary
            // visualMaxX + deltaX <= maxVisualX
            // deltaX <= maxVisualX - visualMaxX
            const maxDeltaXForRight = maxVisualX - initialVisualMaxX;
            maxDeltaX = Math.min(maxDeltaX, maxDeltaXForRight);
          }

          // Check bottom boundary (enforce if canvas is not expandable or max height reached)
          if (enforceBottomBoundary) {
            const maxVisualY = maxCanvasHeight - innerPaddingBottom;
            // Calculate required delta to keep element within bottom boundary
            // visualMaxY + deltaY <= maxVisualY
            // deltaY <= maxVisualY - visualMaxY
            const maxDeltaYForBottom = maxVisualY - initialVisualMaxY;
            maxDeltaY = Math.min(maxDeltaY, maxDeltaYForBottom);
          }
        });

        // Apply constraints to rawDelta
        const constrainedDelta = {
          x: Math.max(minDeltaX, Math.min(maxDeltaX, rawDelta.x)),
          y: Math.max(minDeltaY, Math.min(maxDeltaY, rawDelta.y)),
        };

        // Create a Map with initial positions for all selected elements
        const initialPositionsMap = new Map<string, { x: number; y: number }>();
        selectedElementIds.forEach((id) => {
          const storedPos = multiSelectStartPositionsRef.current.get(id);
          if (storedPos) {
            initialPositionsMap.set(id, storedPos);
          } else {
            // Fallback: use current position
            const el = elements.find((e) => e.id === id);
            if (el) {
              initialPositionsMap.set(id, { ...el.position });
            }
          }
        });

        // Move all selected elements by the constrained delta
        dispatch({
          type: "move-elements",
          elementIds: selectedElementIds,
          delta: constrainedDelta,
          initialPositions: initialPositionsMap,
        });
      } else {
        // Single element move
        dispatch({ type: "move-element", elementId, position });
      }

      // If canvas is expandable, check if element is outside bounds and expand canvas
      // Even if max values are reached, allow elements to go outside canvas
      if (canvas.expandable !== false) {
        const element = elements.find((el) => el.id === elementId);
        if (!element) return;

        const { width: elementWidth, height: elementHeight } =
          resolveElementSizeForBounds(element);
        const { left: visualLeft, right: visualRight, top: visualTop, bottom: visualBottom } =
          getRotatedVisualBounds(
            position,
            { width: elementWidth, height: elementHeight },
            element.rotation
          );

        const innerPaddingLeft = canvas.innerPadding?.left || 0;
        const innerPaddingTop = canvas.innerPadding?.top || 0;
        const innerPaddingRight = canvas.innerPadding?.right || 0;
        const innerPaddingBottom = canvas.innerPadding?.bottom || 0;

        const MAX_HEIGHT = MAX_EDITOR_CANVAS_HEIGHT;

        let needsUpdate = false;
        const canvasChanges: Record<string, number> = {};

        // Yatay genişleme yok - genişlik sabit

        // Top overflow
        if (visualTop < innerPaddingTop) {
          const expandTop = innerPaddingTop - visualTop;
          const newHeight = canvas.height + expandTop;
          if (newHeight <= MAX_HEIGHT) {
            canvasChanges.height = newHeight;
            needsUpdate = true;
          }
        }

        // Bottom overflow
        if (visualBottom > canvas.height - innerPaddingBottom) {
          const newHeight = visualBottom + innerPaddingBottom;
          if (newHeight <= MAX_HEIGHT) {
            canvasChanges.height = newHeight;
            needsUpdate = true;
          }
        }

        if (needsUpdate && Object.keys(canvasChanges).length > 0) {
          dispatch({
            type: "update-canvas",
            changes: canvasChanges,
          });
        }
      }
    },
    [
      dispatch,
      canvas,
      elements,
      selectedElementIds,
      dynamicCanvasSize,
      snapPositionToCanvasGuides,
    ]
  );

  const handleDragEnd = useCallback(() => {
    if (dragHistoryRef.current.active) {
      if (dragHistoryRef.current.started) {
        dispatch({ type: "end-gesture", gesture: "move" });
      }
      dragHistoryRef.current = { active: false, started: false };
    }
  }, [dispatch]);

  const handleResizeElement = useCallback(
    (
      elementId: string,
      size: { width: number; height?: number; minWidth?: number; minHeight?: number }
    ) => {
      const current = elements.find((el) => el.id === elementId);
      const mergedSize = current ? { ...current.size, ...size } : size;
      dispatch({
        type: "update-element",
        elementId,
        changes: { size: mergedSize },
      });

      // If canvas is expandable, check if resized element is outside bounds and expand canvas
      // Even if max values are reached, allow elements to go outside canvas
      if (canvas.expandable !== false) {
        const element = elements.find((el) => el.id === elementId);
        if (!element) return;

        const { width: elementWidth, height: elementHeight } =
          resolveElementSizeForBounds(element, size);
        const { right: visualRight, bottom: visualBottom } =
          getRotatedVisualBounds(
            element.position,
            { width: elementWidth, height: elementHeight },
            element.rotation
          );

        const innerPaddingRight = canvas.innerPadding?.right || 0;
        const innerPaddingBottom = canvas.innerPadding?.bottom || 0;

        const MAX_HEIGHT = MAX_EDITOR_CANVAS_HEIGHT;

        let needsUpdate = false;
        const canvasChanges: Record<string, number> = {};

        // Yatay genişleme yok - genişlik sabit

        // Bottom overflow
        if (visualBottom > canvas.height - innerPaddingBottom) {
          const newHeight = visualBottom + innerPaddingBottom;
          if (newHeight <= MAX_HEIGHT) {
            canvasChanges.height = newHeight;
            needsUpdate = true;
          }
        }

        if (needsUpdate && Object.keys(canvasChanges).length > 0) {
          dispatch({
            type: "update-canvas",
            changes: canvasChanges,
          });
        }
      }
    },
    [dispatch, canvas, elements]
  );

  const handleUpdateElement = useCallback(
    (elementId: string, changes: Partial<EditorElement>) => {
      dispatch({ type: "update-element", elementId, changes });
    },
    [dispatch]
  );

  // Handle choice element changes
  const handleChoiceChange = useCallback(
    (elementId: string, optionId: string, isMultiple: boolean) => {
      // Update formState
      setFormState((prev) => {
        const currentValue = prev[elementId];
        let newValue: string | string[];

        if (isMultiple) {
          // Multiple choice - toggle the option
          const currentArray = Array.isArray(currentValue) ? currentValue : [];
          newValue = currentArray.includes(optionId)
            ? currentArray.filter((id) => id !== optionId)
            : [...currentArray, optionId];
        } else {
          // Single choice - set the option
          newValue = optionId;
        }

        const newFormState = {
          ...prev,
          [elementId]: newValue,
        };

        // Call the callback if provided
        if (onFormStateChange) {
          onFormStateChange(newFormState);
        }

        return newFormState;
      });
    },
    [onFormStateChange]
  );

  const handleDropTargetChange = useCallback(
    (
      targetId: string,
      instanceIds: string | string[],
      sourceId: string | null
    ) => {
      const next = Array.isArray(instanceIds)
        ? instanceIds
        : instanceIds
        ? [instanceIds]
        : [];

      // Update element data
      dispatch({
        type: "update-element",
        elementId: targetId,
        changes: {
          data: {
            droppedItems: next,
          },
        },
      });

      // Update formState and notify external consumers
      setFormState((prev) => {
        const newFormState = {
          ...prev,
          [targetId]: next,
        };

        // Call the callback if provided
        if (onFormStateChange) {
          onFormStateChange(newFormState);
        }

        return newFormState;
      });
    },
    [dispatch, onFormStateChange]
  );

  const handleAddElement = useCallback(
    (template: ElementTemplateKey, dropPosition?: { x: number; y: number }) => {
      const created = createElementsFromTemplate(template);
      if (created.length === 0) {
        return;
      }

      const maxZBase = elements.reduce(
        (acc, element) => Math.max(acc, element.zIndex),
        0
      );

      // Çoklu-eleman şablonu (örn. eşleştirme çiftleri): iç yerleşimi
      // koruyarak tahtayı bir bütün olarak konumlandır.
      if (created.length > 1) {
        const minX = Math.min(...created.map((el) => el.position.x));
        const minY = Math.min(...created.map((el) => el.position.y));
        const maxX = Math.max(
          ...created.map((el) => el.position.x + (el.size.width ?? 0))
        );
        const maxY = Math.max(
          ...created.map(
            (el) => el.position.y + (el.size.height ?? el.size.width ?? 0)
          )
        );
        const boardWidth = maxX - minX;
        const boardHeight = maxY - minY;
        const stagger = dropPosition ? 0 : 24 * elements.length;
        const targetTopLeft = dropPosition
          ? {
              x: Math.round(dropPosition.x - boardWidth / 2),
              y: Math.round(dropPosition.y - boardHeight / 2),
            }
          : { x: minX + stagger, y: minY + stagger };
        const dx = targetTopLeft.x - minX;
        const dy = targetTopLeft.y - minY;
        const laidOut: EditorElement[] = created.map((el, index) => ({
          ...el,
          position: { x: el.position.x + dx, y: el.position.y + dy },
          zIndex: maxZBase + 1 + index,
        }));
        dispatch({ type: "add-elements", elements: laidOut });
        return;
      }

      const newElement = created[0];
      if (!newElement) {
        return;
      }

      // Prevent adding more than one submit button
      // if (newElement.type === "submit-button") {
      //   const hasSubmitButton = elements.some(
      //     (el) => el.type === "submit-button"
      //   );
      //   if (hasSubmitButton) {
      //     alert("Canvas üzerinde yalnızca bir gönder butonu bulunabilir.");
      //     return;
      //   }
      // }

      // Prevent adding more than one timer
      // if (newElement.type === "timer") {
      //   const hasTimer = elements.some((el) => el.type === "timer");
      //   if (hasTimer) {
      //     alert("Canvas üzerinde yalnızca bir zamanlayıcı bulunabilir.");
      //     return;
      //   }
      // }

      const maxZ = elements.reduce(
        (acc, element) => Math.max(acc, element.zIndex),
        0
      );
      const offset = dropPosition ? 0 : 24 * elements.length;
      const basePosition = dropPosition
        ? {
            x: Math.round(dropPosition.x - (newElement.size.width ?? 200) / 2),
            y: Math.round(dropPosition.y - (newElement.size.height ?? 120) / 2),
          }
        : {
            x: newElement.position.x + offset,
            y: newElement.position.y + offset,
          };

      const elementWithLayout: EditorElement = {
        ...newElement,
        position: basePosition,
        zIndex: maxZ + 1,
      };

      dispatch({ type: "add-element", element: elementWithLayout });
    },
    [dispatch, elements]
  );

  const addLocalCanvasMediaFiles = useCallback(
    async (files: File[], insertionPoint?: { x: number; y: number }) => {
      const supportedFiles = files.filter((file) =>
        Boolean(getLocalCanvasMediaKind(file))
      );

      if (supportedFiles.length === 0) {
        toast.error(t("localMedia.unsupported"));
        return false;
      }

      if (supportedFiles.length !== files.length) {
        toast.warning(t("localMedia.partiallyUnsupported"));
      }

      const innerPadding = {
        top: canvas.innerPadding?.top || 0,
        right: canvas.innerPadding?.right || 0,
        bottom: canvas.innerPadding?.bottom || 0,
        left: canvas.innerPadding?.left || 0,
      };
      const stagedElements = [...elements];
      const initialZIndex = elements.reduce(
        (acc, element) => Math.max(acc, element.zIndex),
        0
      );
      const uploadToastId = toast.loading(t("localMedia.uploading"));

      for (let index = 0; index < supportedFiles.length; index += 1) {
        const file = supportedFiles[index];

        try {
          let uploadFile = file;
          try {
            uploadFile = await convertImageFileToWebp(file);
          } catch (conversionError) {
            console.warn("Image to WebP conversion failed:", conversionError);
          }

          const sceneMedia = await apiService.saveSceneMedia(uploadFile);
          const localMediaElement = createLocalCanvasMediaElement(
            uploadFile,
            sceneMedia.url
          );

          if (!localMediaElement) {
            continue;
          }

          const width = localMediaElement.size.width || 200;
          const height = localMediaElement.size.height || 120;
          const stagger = index * 24;
          const preferredPoint = insertionPoint
            ? { x: insertionPoint.x + stagger, y: insertionPoint.y + stagger }
            : {
                x: canvas.width / 2 + stagger,
                y: canvas.height / 2 + stagger,
              };
          const position = findNonOverlappingPosition(
            preferredPoint.x,
            preferredPoint.y,
            width,
            height,
            stagedElements,
            canvas.width,
            canvas.height,
            innerPadding
          );
          const elementWithLayout: EditorElement = {
            ...localMediaElement,
            position: {
              x: Math.round(position.x - width / 2),
              y: Math.round(position.y - height / 2),
            },
            zIndex: initialZIndex + index + 1,
            data: {
              ...localMediaElement.data,
              mediaPath: sceneMedia.path,
              mediaFileName: sceneMedia.file_name,
              sceneMedia,
            },
          };

          stagedElements.push(elementWithLayout);
          dispatch({ type: "add-element", element: elementWithLayout });
        } catch (error) {
          const message =
            error instanceof Error ? error.message : t("localMedia.uploadError");
          toast.error(message || t("localMedia.uploadError"));
        }
      }

      toast.dismiss(uploadToastId);

      return true;
    },
    [canvas, dispatch, elements, t]
  );

  useEffect(() => {
    const handlePaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;

      if (event.defaultPrevented || isTextEntryTarget(event.target)) {
        return;
      }

      const clipboardFiles = Array.from(clipboardEvent.clipboardData?.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file));

      if (clipboardFiles.length === 0) {
        return;
      }

      event.preventDefault();
      addLocalCanvasMediaFiles(clipboardFiles);
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [addLocalCanvasMediaFiles]);

  const addGeneratedAudioElement = useCallback(
    (sourceElement: EditorElement, media: TextToSpeechMedia) => {
      const audioElement = createElementFromTemplate("audio-basic");
      const maxZ = elements.reduce(
        (acc, element) => Math.max(acc, element.zIndex),
        0
      );
      const nextX = Math.max(0, sourceElement.position.x);
      const nextY = Math.max(
        0,
        sourceElement.position.y + (sourceElement.size.height ?? 80) + 16
      );
      const maxX = Math.max(0, canvas.width - audioElement.size.width);
      const maxY = Math.max(
        0,
        canvas.height - (audioElement.size.height ?? 80)
      );
      const label = media.name || media.file_name || t("tts.generatedAudioLabel");

      const elementWithAudio: EditorElement = {
        ...audioElement,
        position: {
          x: Math.min(nextX, maxX),
          y: Math.min(nextY, maxY),
        },
        zIndex: maxZ + 1,
        data: {
          ...audioElement.data,
          style: {
            ...DEFAULT_AUDIO_STYLE,
            ...((audioElement.data as { style?: Partial<AudioStyle> }).style || {}),
            sourceUrl: media.url,
            label,
            showLabel: false,
            showControls: true,
          },
          generatedFromTextElementId: sourceElement.id,
          mediaId: media.media_id,
          mediaPath: media.path,
          mediaFileName: media.file_name,
          mediaMetadata: media.metadata,
        },
      };

      dispatch({ type: "add-element", element: elementWithAudio });
      dispatch({ type: "select-elements", elementIds: [elementWithAudio.id] });
    },
    [canvas.height, canvas.width, dispatch, elements, t]
  );

  const handleOpenTextToSpeech = useCallback(
    (elementId: string) => {
      const element = elements.find((candidate) => candidate.id === elementId);
      const speechText = getElementSpeechText(element);

      if (!speechText) {
        toast.error(t("tts.emptyTextError"));
        return;
      }

      setTextToSpeechElementId(elementId);
    },
    [elements, t]
  );

  const handleSubmitTextToSpeech = useCallback(
    async (payload: TextToSpeechSubmitPayload) => {
      const element = elements.find(
        (candidate) => candidate.id === textToSpeechElementId
      );
      const speechText = getElementSpeechText(element);

      if (!element || !speechText) {
        toast.error(t("tts.emptyTextError"));
        return;
      }

      try {
        setIsTextToSpeechSubmitting(true);
        const media = await apiService.generateSpeech({
          text: speechText,
          ...payload,
        });

        if (!media.url) {
          throw new Error(t("tts.missingUrlError"));
        }

        addGeneratedAudioElement(element, media);
        setTextToSpeechElementId(null);
        toast.success(t("tts.success"));
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : t("tts.error");
        toast.error(message);
      } finally {
        setIsTextToSpeechSubmitting(false);
      }
    },
    [addGeneratedAudioElement, elements, t, textToSpeechElementId]
  );

  const handlePermanentDeleteAudio = useCallback(
    async (elementId: string) => {
      const element = elements.find((candidate) => candidate.id === elementId);
      const path = getAudioElementMediaPath(element);

      if (!path) {
        toast.error(t("tts.deleteMissingPathError"));
        return;
      }

      const confirmed = window.confirm(t("tts.deleteConfirm"));
      if (!confirmed) {
        return;
      }

      try {
        await apiService.deleteGeneratedSpeech({ path });
        dispatch({ type: "delete-element", elementId });
        toast.success(t("tts.deleteSuccess"));
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : t("tts.deleteError");
        toast.error(message);
      }
    },
    [dispatch, elements, t]
  );

  // Place a TTS-generated audio element exactly behind its source text element
  // (same bounds, lower z-index) and make it auto-play on hover in preview.
  const handlePlaceAudioBehindText = useCallback(
    (elementId: string) => {
      const audioElement = elements.find((el) => el.id === elementId);
      if (!audioElement || audioElement.type !== "audio") {
        return;
      }
      const data = audioElement.data as {
        generatedFromTextElementId?: string;
        style?: Record<string, unknown>;
      };
      const textElement = data.generatedFromTextElementId
        ? elements.find((el) => el.id === data.generatedFromTextElementId)
        : undefined;
      if (!textElement) {
        toast.error(t("contextMenu.placeBehindTextMissing"));
        return;
      }

      dispatch({
        type: "update-element",
        elementId,
        changes: {
          position: { ...textElement.position },
          size: {
            ...textElement.size,
            height: textElement.size.height ?? textElement.size.minHeight ?? 60,
          },
          rotation: textElement.rotation,
          zIndex: textElement.zIndex - 1,
          data: {
            ...audioElement.data,
            style: {
              ...(data.style || {}),
              showControls: false,
              showIcon: false,
              showLabel: false,
              autoPlay: false,
              playOnHover: true,
              backgroundColor: "transparent",
              borderWidth: 0,
            },
          },
        },
      });
      dispatch({ type: "select-elements", elementIds: [textElement.id] });
      toast.success(t("contextMenu.placeBehindTextDone"));
    },
    [dispatch, elements, t]
  );

  const handleDeleteSelected = useCallback(() => {
    if (selectedElementIds.length > 1) {
      dispatch({ type: "delete-elements", elementIds: selectedElementIds });
      return;
    }
    const targetId = selectedElementIds[0] || selectedElementId;
    if (!targetId) {
      return;
    }
    dispatch({ type: "delete-element", elementId: targetId });
  }, [dispatch, selectedElementIds, selectedElementId]);

  // Internal clipboard for copy/cut/paste of canvas elements (in-app, not OS clipboard)
  const clipboardRef = useRef<EditorElement[]>([]);

  const handleCopySelected = useCallback(() => {
    const selected = elements.filter((el) =>
      selectedElementIds.includes(el.id)
    );
    if (selected.length === 0) {
      return;
    }
    clipboardRef.current = JSON.parse(
      JSON.stringify(selected)
    ) as EditorElement[];
  }, [elements, selectedElementIds]);

  const handlePasteClipboard = useCallback(() => {
    const clip = clipboardRef.current;
    if (!clip || clip.length === 0) {
      return;
    }
    const maxZIndex =
      elements.length > 0
        ? Math.max(...elements.map((el) => el.zIndex || 0), 0)
        : 0;

    const pasted: EditorElement[] = clip.map((source, index) => {
      const cloned = JSON.parse(JSON.stringify(source)) as EditorElement;
      cloned.id = generateElementId(cloned.type);
      cloned.position = {
        x: Math.min(
          source.position.x + 20,
          dynamicCanvasSize.width - (source.size.width || 0)
        ),
        y: Math.min(
          source.position.y + 20,
          dynamicCanvasSize.height - (source.size.height || source.size.width || 40)
        ),
      };
      cloned.zIndex = maxZIndex + 1 + index;
      // Groups carry their children with their own ids — regenerate those too so
      // pasting a group does not duplicate child ids.
      if (cloned.type === "group") {
        const data = cloned.data as unknown as GroupData;
        cloned.data = {
          ...(cloned.data as Record<string, unknown>),
          children: (data.children || []).map((child) => ({
            ...child,
            id: generateElementId(child.type),
          })),
        } as unknown as Record<string, unknown>;
      }
      return cloned;
    });

    dispatch({ type: "add-elements", elements: pasted });
    // Advance the clipboard to the pasted positions so repeated pastes cascade
    // instead of stacking on the same spot.
    clipboardRef.current = JSON.parse(JSON.stringify(pasted)) as EditorElement[];
  }, [elements, dynamicCanvasSize, dispatch]);

  const handleCutSelected = useCallback(() => {
    const selected = elements.filter((el) =>
      selectedElementIds.includes(el.id)
    );
    if (selected.length === 0) {
      return;
    }
    clipboardRef.current = JSON.parse(
      JSON.stringify(selected)
    ) as EditorElement[];
    dispatch({
      type: "delete-elements",
      elementIds: selected.map((el) => el.id),
    });
  }, [elements, selectedElementIds, dispatch]);

  const handleGroupSelected = useCallback(() => {
    if (selectedElementIds.length < 2) {
      return;
    }
    // The reducer rejects the action if any selected element is already a group.
    dispatch({ type: "group-elements", elementIds: selectedElementIds });
  }, [selectedElementIds, dispatch]);

  const handleUngroupSelected = useCallback(() => {
    const selectedGroupIds = elements
      .filter(
        (el) => el.type === "group" && selectedElementIds.includes(el.id)
      )
      .map((el) => el.id);
    selectedGroupIds.forEach((groupId) =>
      dispatch({ type: "ungroup-element", groupId })
    );
  }, [elements, selectedElementIds, dispatch]);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const toggleQuestionSettingsBar = useCallback(() => {
    setQuestionSettingsBarVisible((current) => !current);
  }, []);

  const handleUpdateQuestionMeta = useCallback(
    (patch: Partial<NonNullable<CanvasSettings["question"]>>) => {
      dispatch({
        type: "update-canvas",
        // Metadata edits are committed live for quick entry and persisted via
        // the header Save button; keep them out of the canvas undo history.
        origin: "system",
        changes: {
          question: {
            ...canvas.question,
            ...patch,
          },
        },
      });
    },
    [dispatch, canvas.question]
  );

  const openAnswerKey = useCallback(() => {
    setAnswerKeyOpen(true);
  }, []);

  const closeAnswerKey = useCallback(() => {
    setAnswerKeyOpen(false);
  }, []);

  const handleSaveQuestionSettings = useCallback(
    (updates: Partial<CanvasSettings>) => {
      dispatch({
        type: "update-canvas",
        changes: updates,
      });
    },
    [dispatch]
  );

  const applyCanvasSettings = useCallback(
    (settings: CanvasSettings) => {
      dispatch({
        type: "update-canvas",
        changes: {
          width: settings.width,
          height: settings.height,
          lockWidth: settings.lockWidth,
          background: settings.background,
          expandable: settings.expandable,
          padding: { ...settings.padding },
          innerPadding: settings.innerPadding
            ? { ...settings.innerPadding }
            : undefined,
          grid: { ...settings.grid },
          hiddenArea: { ...settings.hiddenArea },
        },
      });
    },
    [dispatch]
  );

  const handleSaveSettings = useCallback(
    (settings: CanvasSettings) => {
      applyCanvasSettings(settings);
      setSettingsOpen(false);
    },
    [applyCanvasSettings]
  );

  const handleResetSettings = useCallback(
    (settings: CanvasSettings) => {
      const source = cloneCanvasSettings(settings);
      applyCanvasSettings(source);
    },
    [applyCanvasSettings]
  );

  const openPreview = useCallback(() => {
    // Groups are a design-time aid and are unstable in preview/question paths;
    // dissolve them all before entering preview so none remain.
    dispatch({ type: "ungroup-all" });
    setPreviewOpen(true);
  }, [dispatch]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);

  const handleSaveSuccess = useCallback((message: string) => {
    toast.success(message);
  }, []);

  const handleSaveError = useCallback((message: string) => {
    toast.error(message);
  }, []);

  const handleContinueEditing = useCallback(() => {
    setSaveSuccessModal({ isOpen: false, message: "" });
  }, []);

  const handleRedirectToQuestions = useCallback(() => {
    setSaveSuccessModal({ isOpen: false, message: "" });
    redirectToQuestions();
  }, [redirectToQuestions]);

  const handleTemplateSelect = useCallback(
    async (template: Template) => {
      try {
        toast.loading(`"${template.title}" ${t("toast.templateLoading")}`);
        const response = await apiService.getTemplate(template.id);
        const templateData = (response as any)?.data?.question ?? response;

        // Check if answerKey is at the top level
        const topLevelAnswerKey = (response as any)?.data?.answerKey;

        // Merge answerKey if it exists at top level
        const payload = topLevelAnswerKey
          ? { ...templateData, answerKey: topLevelAnswerKey }
          : templateData;

        if (!payload) {
          toast.dismiss();
          toast.error(t("toast.templateDataNotFound"));
          return;
        }

        const normalized = normalizeQuestionPayload(payload);

        // Şablonun soru tipini ve mevcut soru tipini karşılaştır
        const templateQuestionTypeId =
          normalized.canvas.question?.questionType?.id;
        const currentQuestionTypeId = canvas.question?.questionType?.id;
        const questionTypesMatch =
          templateQuestionTypeId &&
          currentQuestionTypeId &&
          templateQuestionTypeId === currentQuestionTypeId;

        // Soru tipleri farklıysa: Şablonun soru tipini ve tüm soru ayarlarını uygula (answerKey dahil)
        // Soru tipleri aynıysa: Mevcut soru ayarlarını koru (questionType dahil), answerKey'i koru
        const finalQuestion = questionTypesMatch
          ? {
              ...canvas.question, // Mevcut soru ayarlarını koru
              questionType: canvas.question?.questionType, // Mevcut soru tipini koru
              answerKey:
                canvas.question?.answerKey ||
                normalized.canvas.question?.answerKey, // Mevcut answerKey'i koru, yoksa şablonun answerKey'ini kullan
            }
          : normalized.canvas.question; // Şablonun soru tipini ve ayarlarını uygula (answerKey dahil)

        const stateWithPreservedQuestion = {
          ...normalized,
          canvas: {
            ...normalized.canvas,
            question: finalQuestion,
          },
        };

        dispatch({
          type: "set-state",
          state: stateWithPreservedQuestion,
        });

        toast.dismiss();
        toast.success(`"${template.title}" ${t("toast.templateLoaded")}`);
      } catch (error) {
        toast.dismiss();
        toast.error(t("toast.templateLoadError"));
        console.error("Template load error:", error);
      }
    },
    [dispatch, canvas]
  );

  const saveAsDraft = useCallback(
    async (options?: { forceCreate?: boolean }) => {
      // Flatten groups: design-time aid, not persisted.
      const flatElements = flattenGroups(elements);

      // Validate canvas requirements
      const validation = validateCanvasRequirements(flatElements);
      if (!validation.isValid) {
        handleSaveError(validation.message);
        return;
      }

      // Dissolve groups in the editor too, so none remain after saving.
      dispatch({ type: "ungroup-all" });

      setIsSaving(true);

      try {
        // Canvas screenshot'ını al (hem base64 hem File formatında)
        // Not: captureCanvasScreenshot artık resimlerin yüklenmesini bekler ve legacy MathLive text node'larını gizler
        const { base64: screenshot, file: screenshotFile } =
          await captureCanvasScreenshotBothFormats(canvasRef.current, {
            quality: 0.92,
            format: "png",
            scale: 1,
            filename: `canvas-screenshot-${Date.now()}.png`,
          });

        const shouldForceCreate = Boolean(options?.forceCreate);
        const isUpdating = Boolean(activeQuestionId) && !shouldForceCreate;
        const questionData = ApiService.formatEditorStateToQuestionData(
          { ...currentEditorState, elements: flatElements },
          "draft",
          screenshotFile,
          screenshot,
          undefined,
          isUpdating,
          persistedQuestionType
        );
        const response =
          isUpdating && activeQuestionId
            ? await apiService.updateQuestion(activeQuestionId, questionData)
            : await apiService.saveQuestion(questionData);

        if (response.success) {
          const successMessage = shouldForceCreate
            ? t("toast.draftCreated")
            : isUpdating
            ? t("toast.draftUpdated")
            : t("toast.draftSaved");
          syncQuestionIdFromResponse(response);
          setPersistedQuestionType("draft");
          lastSavedSnapshotRef.current = JSON.stringify({
            canvas,
            elements: flatElements,
            assets,
          });
          setSaveSuccessModal({ isOpen: true, message: successMessage });
        } else {
          handleSaveError(response.message || t("toast.draftSaveFailed"));
        }
      } catch (_error) {
        handleSaveError(t("toast.draftSaveError"));
      } finally {
        setIsSaving(false);
      }
      setSaveMenuOpen(false);
    },
    [
      currentEditorState,
      handleSaveSuccess,
      handleSaveError,
      elements,
      activeQuestionId,
      syncQuestionIdFromResponse,
      persistedQuestionType,
      dispatch,
    ]
  );

  const saveAsTemplate = useCallback(
    async (options?: { forceCreate?: boolean; name?: string; tags?: string[] }) => {
      // Flatten groups: design-time aid, not persisted.
      const flatElements = flattenGroups(elements);

      // Validate canvas requirements
      const validation = validateCanvasRequirements(flatElements);
      if (!validation.isValid) {
        handleSaveError(validation.message);
        return;
      }

      // Dissolve groups in the editor too, so none remain after saving.
      dispatch({ type: "ungroup-all" });

      try {
        // Canvas screenshot'ını al (hem base64 hem File formatında)
        // Not: captureCanvasScreenshot artık resimlerin yüklenmesini bekler ve legacy MathLive text node'larını gizler
        const { base64: screenshot, file: screenshotFile } =
          await captureCanvasScreenshotBothFormats(canvasRef.current, {
            quality: 0.92,
            format: "png",
            scale: 1,
            filename: `canvas-screenshot-${Date.now()}.png`,
          });

        const shouldForceCreate = Boolean(options?.forceCreate);
        const isUpdating = Boolean(activeQuestionId) && !shouldForceCreate;
        const resolvedTags = options?.tags ?? canvas.question?.tags;
        if (options?.tags) {
          dispatch({
            type: "update-canvas",
            changes: { question: { tags: options.tags } },
          });
        }

        const questionData = ApiService.formatEditorStateToQuestionData(
          { ...currentEditorState, elements: flatElements },
          "template",
          screenshotFile,
          screenshot,
          options?.name,
          isUpdating,
          persistedQuestionType,
          resolvedTags
        );
        const response =
          isUpdating && activeQuestionId
            ? await apiService.updateQuestion(activeQuestionId, questionData)
            : await apiService.saveQuestion(questionData);

        if (response.success) {
          handleSaveSuccess(
            shouldForceCreate
              ? t("toast.templateCreated")
              : isUpdating
              ? t("toast.templateUpdated")
              : t("toast.templateSaved")
          );
          fetchDefinitions();
          syncQuestionIdFromResponse(response);
          setPersistedQuestionType("template");
          lastSavedSnapshotRef.current = JSON.stringify({
            canvas,
            elements: flatElements,
            assets,
          });
        } else {
          handleSaveError(response.message || t("toast.templateSaveFailed"));
        }
      } catch (_error) {
        handleSaveError(t("toast.templateSaveError"));
      }
      setSaveMenuOpen(false);
    },
    [
      currentEditorState,
      handleSaveSuccess,
      handleSaveError,
      elements,
      canvas.question?.tags,
      activeQuestionId,
      syncQuestionIdFromResponse,
      persistedQuestionType,
      fetchDefinitions,
      dispatch,
    ]
  );

  const saveNormal = useCallback(
    async (options?: { forceCreate?: boolean }) => {
      if (!isQuestionSettingsComplete) {
        handleSaveError(t("toast.normalSaveSettingsRequired"));
        return;
      }

      // Groups are a design-time aid; flatten them so validation and the saved
      // data see the real elements and no grouped object is persisted.
      const flatElements = flattenGroups(elements);

      // Validate canvas requirements
      const validation = validateCanvasRequirements(flatElements);
      if (!validation.isValid) {
        handleSaveError(validation.message);
        return;
      }

      // Validate answer key for normal save
      const answerKeyValidation = validateAnswerKey(
        flatElements,
        canvas.question?.answerKey
      );
      if (!answerKeyValidation.isValid) {
        handleSaveError(answerKeyValidation.message);
        return;
      }

      // Dissolve groups in the editor too, so none remain after saving.
      dispatch({ type: "ungroup-all" });

      setIsSaving(true);

      try {
        // Canvas screenshot'ını al (hem base64 hem File formatında)
        // Not: captureCanvasScreenshot artık resimlerin yüklenmesini bekler ve legacy MathLive text node'larını gizler
        const { base64: screenshot, file: screenshotFile } =
          await captureCanvasScreenshotBothFormats(canvasRef.current, {
            quality: 0.92,
            format: "png",
            scale: 1,
            filename: `canvas-screenshot-${Date.now()}.png`,
          });

        const shouldForceCreate = Boolean(options?.forceCreate);
        const isUpdating = Boolean(activeQuestionId) && !shouldForceCreate;
        const questionData = ApiService.formatEditorStateToQuestionData(
          { ...currentEditorState, elements: flatElements },
          "question",
          screenshotFile,
          screenshot,
          undefined,
          isUpdating,
          persistedQuestionType
        );
        const response =
          isUpdating && activeQuestionId
            ? await apiService.updateQuestion(activeQuestionId, questionData)
            : await apiService.saveQuestion(questionData);

        if (response.success) {
          const successMessage = shouldForceCreate
            ? t("toast.questionCreated")
            : isUpdating
            ? t("toast.questionUpdated")
            : t("toast.questionSaved");
          syncQuestionIdFromResponse(response);
          setPersistedQuestionType("question");
          // Update snapshot after successful save (groups already flattened).
          lastSavedSnapshotRef.current = JSON.stringify({
            canvas,
            elements: flatElements,
            assets,
          });
          setSaveSuccessModal({ isOpen: true, message: successMessage });
        } else {
          handleSaveError(response.message || t("toast.questionSaveFailed"));
        }
      } catch (_error) {
        handleSaveError(t("toast.questionSaveError"));
      } finally {
        setIsSaving(false);
      }
      setSaveMenuOpen(false);
    },
    [
      currentEditorState,
      handleSaveSuccess,
      handleSaveError,
      elements,
      canvas.question?.answerKey,
      isQuestionSettingsComplete,
      activeQuestionId,
      syncQuestionIdFromResponse,
      persistedQuestionType,
      dispatch,
    ]
  );

  useEffect(() => {
    if (!selectedElementId || isPreviewOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!selectedElementId) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target) {
        const tagName = target.tagName.toLowerCase();
        const isEditable =
          target.isContentEditable ||
          tagName === "input" ||
          tagName === "textarea" ||
          tagName === "select";
        if (isEditable) {
          return;
        }
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        handleDeleteSelected();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDeleteSelected, selectedElementId, isPreviewOpen]);

  useEffect(() => {
    if (isPreviewOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target) {
        const tagName = target.tagName.toLowerCase();
        const isEditable =
          target.isContentEditable ||
          tagName === "input" ||
          tagName === "textarea" ||
          tagName === "select";
        if (isEditable) {
          return;
        }
      }

      const isModKey = event.metaKey || event.ctrlKey;
      if (!isModKey) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? "redo" : "undo" });
        return;
      }
      if (key === "y") {
        event.preventDefault();
        dispatch({ type: "redo" });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch, isPreviewOpen]);

  // Copy / Cut / Paste / Group shortcuts (Ctrl/Cmd + C / X / V / G)
  useEffect(() => {
    if (isPreviewOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target) {
        const tagName = target.tagName.toLowerCase();
        const isEditable =
          target.isContentEditable ||
          tagName === "input" ||
          tagName === "textarea" ||
          tagName === "select";
        if (isEditable) {
          return;
        }
      }

      const isModKey = event.metaKey || event.ctrlKey;
      if (!isModKey) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === "c") {
        event.preventDefault();
        handleCopySelected();
      } else if (key === "x") {
        event.preventDefault();
        handleCutSelected();
      } else if (key === "v") {
        // Only intercept when we have in-app elements to paste; otherwise let the
        // document-level paste handler deal with media files from the OS clipboard.
        if (clipboardRef.current.length > 0) {
          event.preventDefault();
          handlePasteClipboard();
        }
      } else if (key === "g") {
        // Cmd/Ctrl+G groups; Cmd/Ctrl+Shift+G ungroups the selected group(s).
        event.preventDefault();
        if (event.shiftKey) {
          handleUngroupSelected();
        } else {
          handleGroupSelected();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    isPreviewOpen,
    handleCopySelected,
    handleCutSelected,
    handlePasteClipboard,
    handleGroupSelected,
    handleUngroupSelected,
  ]);

  useEffect(() => {
    if (!isPreviewOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closePreview();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewOpen, closePreview]);

  useEffect(() => {
    if (!isPreviewOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPreviewOpen]);

  const handleDragOverCanvas = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const hasTemplate = event.dataTransfer?.types.includes(TEMPLATE_MIME);
      const hasFiles =
        event.dataTransfer?.types.includes("Files") ||
        Array.from(event.dataTransfer?.items || []).some(
          (item) => item.kind === "file"
        );

      if (!hasTemplate && !hasFiles) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setLocalMediaDragActive(hasFiles);
    },
    []
  );

  const handleDragLeaveCanvas = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
        return;
      }

      setLocalMediaDragActive(false);
    },
    []
  );

  const handleDropOnCanvas = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      setLocalMediaDragActive(false);

      const droppedFiles = Array.from(event.dataTransfer?.files || []);
      if (droppedFiles.length > 0) {
        event.preventDefault();

        const canvasRect = canvasRef.current?.getBoundingClientRect();
        const insertionPoint = canvasRect
          ? {
              x: (event.clientX - canvasRect.left) / zoom,
              y: (event.clientY - canvasRect.top) / zoom,
            }
          : undefined;

        addLocalCanvasMediaFiles(droppedFiles, insertionPoint);
        return;
      }

      const templateId = event.dataTransfer?.getData(TEMPLATE_MIME) as
        | ElementTemplateKey
        | "";
      if (!templateId) {
        return;
      }

      event.preventDefault();

      // Canvas'ın rect'ini al - hem canvas hem de shell'den drop gelebilir
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) {
        return;
      }

      // Drop koordinatlarını canvas'a göre hesapla
      let dropX = (event.clientX - canvasRect.left) / zoom;
      let dropY = (event.clientY - canvasRect.top) / zoom;

      // Element template'ini alarak boyutunu öğren
      const tempElement = createElementFromTemplate(templateId);
      if (!tempElement) {
        return;
      }

      const elementWidth = tempElement.size.width || 200;
      const elementHeight = tempElement.size.height || 120;

      // İç boşluk değerlerini al
      const innerPaddingLeft = canvas.innerPadding?.left || 0;
      const innerPaddingTop = canvas.innerPadding?.top || 0;
      const innerPaddingRight = canvas.innerPadding?.right || 0;
      const innerPaddingBottom = canvas.innerPadding?.bottom || 0;

      // Akıllı yerleştirme: Overlap olmayan en uygun pozisyonu bul
      const optimalPosition = findNonOverlappingPosition(
        dropX,
        dropY,
        elementWidth,
        elementHeight,
        elements,
        canvas.width,
        canvas.height,
        {
          top: innerPaddingTop,
          right: innerPaddingRight,
          bottom: innerPaddingBottom,
          left: innerPaddingLeft,
        }
      );

      // Optimal pozisyonu kullan
      dropX = optimalPosition.x;
      dropY = optimalPosition.y;

      // Elementin sol üst köşesi
      let elementLeft = dropX - elementWidth / 2;
      let elementTop = dropY - elementHeight / 2;

      const elementRight = elementLeft + elementWidth;
      const elementBottom = elementTop + elementHeight;

      // Genişlik sabit - elementi canvas genişliği içinde tut
      let newCanvasHeight = canvas.height;

      if (canvas.expandable !== false) {
        // Yatay: elementi sabit genişlik içinde tut
        if (elementLeft < innerPaddingLeft) {
          elementLeft = innerPaddingLeft;
          dropX = elementLeft + elementWidth / 2;
        }
        if (elementRight > canvas.width - innerPaddingRight) {
          elementLeft = canvas.width - elementWidth - innerPaddingRight;
          dropX = elementLeft + elementWidth / 2;
        }

        // Yukarı taşma
        if (elementTop < 0) {
          const expandTop = Math.abs(elementTop);
          newCanvasHeight += expandTop;
          dropY += expandTop;
        }

        // Aşağı taşma
        if (elementBottom > canvas.height) {
          newCanvasHeight = elementBottom;
        }

        // Sadece yükseklik değiştiyse güncelle
        if (newCanvasHeight !== canvas.height) {
          const canvasChanges: Record<string, number> = {
            height: Math.max(DEFAULT_CANVAS_SETTINGS.height, newCanvasHeight),
          };

          dispatch({
            type: "update-canvas",
            changes: canvasChanges,
          });

          // Mevcut elementlerin pozisyonlarını güncelle (canvas yukarı genişlediyse)
          if (elementTop < 0) {
            const offsetY = Math.abs(elementTop);
            elements.forEach((element) => {
              dispatch({
                type: "update-element",
                elementId: element.id,
                changes: {
                  position: {
                    x: element.position.x,
                    y: element.position.y + offsetY,
                  },
                },
              });
            });
          }
        }
      }

      // Elementi ekle (dropX ve dropY iç boşluk sınırlarıyla güncellenmiş durumda)
      handleAddElement(templateId, { x: dropX, y: dropY });
    },
    [addLocalCanvasMediaFiles, handleAddElement, zoom, canvas, dispatch, elements]
  );

  const handleContextMenu = useCallback(
    (elementId: string, x: number, y: number, hasTextSelection?: boolean) => {
      setContextMenu({ x, y, elementId, hasTextSelection });
    },
    []
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Close context menu on any interaction (except clicks inside the menu)
  useEffect(() => {
    if (!contextMenu) return;

    const handleAnyInteraction = (e: Event) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside the context menu
      if (target.closest(".qe-context-menu")) {
        return;
      }
      setContextMenu(null);
    };

    // Close on scroll, pointer events, or any canvas interaction
    window.addEventListener("scroll", handleAnyInteraction, true);
    window.addEventListener("pointerdown", handleAnyInteraction, true);

    return () => {
      window.removeEventListener("scroll", handleAnyInteraction, true);
      window.removeEventListener("pointerdown", handleAnyInteraction, true);
    };
  }, [contextMenu]);

  // Close save menu when clicking outside
  useEffect(() => {
    if (!isSaveMenuOpen) return;

    const handleClickOutside = (e: Event) => {
      const target = e.target as HTMLElement;
      // Don't close if clicking inside the save dropdown
      if (
        target.closest(".qe-dropdown") ||
        target.closest(".qe-dropdown__menu")
      ) {
        return;
      }
      setSaveMenuOpen(false);
    };

    window.addEventListener("click", handleClickOutside, true);

    return () => {
      window.removeEventListener("click", handleClickOutside, true);
    };
  }, [isSaveMenuOpen]);

  const handleCloneElement = useCallback(
    (elementId: string) => {
      // First, try to find in global elements
      const element = elements.find((el) => el.id === elementId);

      if (element) {
        // Clone regular element
        const newElement: EditorElement = {
          ...element,
          id: generateElementId(element.type),
          position: {
            x: Math.min(
              element.position.x + 20,
              dynamicCanvasSize.width - element.size.width
            ),
            y: Math.min(
              element.position.y + 20,
              dynamicCanvasSize.height - (element.size.height || 40)
            ),
          },
          zIndex: Math.max(...elements.map((el) => el.zIndex), 0) + 1,
        };

        dispatch({ type: "add-element", element: newElement });
        dispatch({ type: "select-element", elementId: newElement.id });
        return;
      }

      // If not found in global elements, check if it's a cell element
      for (const tableElement of elements) {
        if (tableElement.type === "table") {
          const cellElements = (tableElement.data as any).cellElements as
            | Record<string, EditorElement>
            | undefined;
          if (cellElements && cellElements[elementId]) {
            const cellElement = cellElements[elementId];
            const cells = (tableElement.data as any).cells as any[] | undefined;

            // Find which cell contains this element
            const parentCell = cells?.find((cell: any) =>
              cell.elements?.includes(elementId)
            );

            if (parentCell) {
              // Clone the cell element with a new ID and slightly offset position
              const newCellElement: EditorElement = {
                ...cellElement,
                id: generateElementId(cellElement.type),
                position: {
                  x: Math.min(
                    cellElement.position.x + 10,
                    cellElement.position.x + 20
                  ),
                  y: Math.min(
                    cellElement.position.y + 10,
                    cellElement.position.y + 20
                  ),
                },
              };

              // Add the cloned element to the same cell
              const updatedCellElements = {
                ...cellElements,
                [newCellElement.id]: newCellElement,
              };

              const updatedCells = cells!.map((cell: any) =>
                cell.id === parentCell.id
                  ? { ...cell, elements: [...cell.elements, newCellElement.id] }
                  : cell
              );

              dispatch({
                type: "update-element",
                elementId: tableElement.id,
                changes: {
                  data: {
                    ...tableElement.data,
                    cellElements: updatedCellElements,
                    cells: updatedCells,
                    selectedCellElementId: newCellElement.id,
                  },
                },
              });

              // Select the table so the new cell element's properties appear
              dispatch({ type: "select-element", elementId: tableElement.id });
              return;
            }
          }
        }
      }
    },
    [elements, dynamicCanvasSize, dispatch]
  );

  const handleAlignCanvas = useCallback(
    (alignment: "left" | "center" | "right") => {
      if (!stageRef.current) return;

      const stageRect = stageRef.current.getBoundingClientRect();
      const stageWidth = stageRect.width;
      const canvasWidth = dynamicCanvasSize.width;
      const defaultPaddingY = canvas.padding?.y || 32;

      let newPaddingX: number;

      switch (alignment) {
        case "left":
          newPaddingX = 32; // Default left padding
          break;
        case "center":
          newPaddingX = Math.max(32, (stageWidth - canvasWidth) / 2);
          break;
        case "right":
          newPaddingX = Math.max(32, stageWidth - canvasWidth - 32);
          break;
        default:
          newPaddingX = 32;
      }

      dispatch({
        type: "update-canvas",
        changes: {
          padding: {
            x: Math.round(newPaddingX),
            y: defaultPaddingY,
          },
        },
      });
    },
    [dynamicCanvasSize.width, canvas.padding, dispatch]
  );

  const handleClearCanvas = useCallback(() => {
    if (elements.length === 0) {
      return;
    }

    if (
      window.confirm(t("confirm.clearCanvas"))
    ) {
      // Clear all elements by deleting them one by one
      // This ensures answer keys are properly cleaned up
      elements.forEach((element) => {
        dispatch({
          type: "delete-element",
          elementId: element.id,
        });
      });
      toast.success(t("toast.canvasCleared"));
    }
  }, [elements, dispatch]);

  const shouldShowPageOverlay = isRedirecting || !isInitialLoadComplete;
  const pageOverlayMessage = isRedirecting
    ? t("overlay.redirecting")
    : t("overlay.loading");
  const pageOverlay = shouldShowPageOverlay ? (
    <div className="qe-redirect-overlay">
      <div className="qe-redirect-overlay__content">
        <div className="qe-redirect-overlay__spinner">
          <div className="qe-redirect-overlay__spinner-circle"></div>
        </div>
        <p className="qe-redirect-overlay__message">{pageOverlayMessage}</p>
      </div>
    </div>
  ) : null;

  if (isRedirecting || !isInitialLoadComplete) {
    return (
      <div className="qe-app">
        <Toaster position="top-center" />
        {pageOverlay}
      </div>
    );
  }

  if (isPreviewMode) {
    return (
      <>
        <Toaster position="top-center" />
        {pageOverlay}
        <PreviewOnlyScreen
          canvas={canvas}
          elements={elements}
          hiddenElements={hiddenElements}
          isLoading={isQuestionLoading || !isQuestionLoaded}
          error={questionLoadError}
          missingQuestion={!activeQuestionId}
        />
      </>
    );
  }

  return (
    <div className="qe-app">
      <Toaster position="top-center" />
      {pageOverlay}
      {isSaving && (
        <div className="qe-saving-overlay">
          <div className="qe-saving-overlay__content">
            <div className="qe-saving-overlay__spinner">
              <svg className="qe-saving-overlay__spinner-svg" viewBox="0 0 50 50">
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.15)"
                  strokeWidth="4"
                />
                <circle
                  className="qe-saving-overlay__spinner-circle"
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  strokeWidth="4"
                />
              </svg>
            </div>
            <p className="qe-saving-overlay__message">Kaydediliyor...</p>
          </div>
        </div>
      )}
      <header className="qe-header">
        <div className="qe-header__title">{t("header.title")}</div>
        <div className="qe-header__actions">
          <button
            className="qe-button qe-icon-button"
            type="button"
            onClick={handleClearCanvas}
            title={t("header.clearCanvas")}
            aria-label={t("header.clearCanvas")}
            disabled={elements.length === 0}
          >
            <EraserIcon />
          </button>
          <button
            className="qe-button qe-icon-button"
            type="button"
            onClick={openSettings}
            title={t("header.canvasSettings")}
            aria-label={t("header.canvasSettings")}
          >
            <SettingsIcon />
          </button>
          <button
            className={`qe-button qe-button--ghost ${
              isQuestionSettingsBarVisible ? "qe-button--active" : ""
            }`}
            type="button"
            onClick={toggleQuestionSettingsBar}
            disabled={isQuestionLoading}
            aria-pressed={isQuestionSettingsBarVisible}
          >
            {t("header.questionSettings")}
          </button>
          <button
            className="qe-button qe-button--ghost"
            type="button"
            onClick={openAnswerKey}
            disabled={isQuestionLoading}
          >
            {t("header.answerKey")}
          </button>
          <div className="qe-header__separator" />
          <button className="qe-button" type="button" onClick={openPreview}>
            {t("header.preview")}
          </button>
          <div className="qe-dropdown" style={{ position: "relative" }}>
            <button
              className="qe-button qe-button--primary"
              type="button"
              disabled={isQuestionLoading}
              onClick={() => {
                if (isQuestionLoading) {
                  return;
                }
                setSaveMenuOpen(!isSaveMenuOpen);
              }}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              {t("header.save")}
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {isSaveMenuOpen &&
              createPortal(
                (() => {
                  const renderMenuItem = ({
                    key,
                    title,
                    subtitle,
                    iconBackground,
                    iconIndicator,
                    onClick,
                    disabled = false,
                    subtitleColor,
                  }: {
                    key: string;
                    title: string;
                    subtitle: string;
                    iconBackground: string;
                    iconIndicator: string;
                    onClick: () => void;
                    disabled?: boolean;
                    subtitleColor?: string;
                  }) => (
                    <button
                      key={key}
                      className="qe-dropdown__item"
                      type="button"
                      disabled={disabled || isQuestionLoading}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (disabled || isQuestionLoading) {
                          return;
                        }
                        onClick();
                      }}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        cursor: disabled ? "not-allowed" : "pointer",
                        fontSize: "14px",
                        color: disabled ? "#9ca3af" : "#111827",
                        borderRadius: "6px",
                        transition: "all 0.15s ease",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        opacity: disabled ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (disabled) return;
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "4px",
                          background: iconBackground,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      >
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius:
                              iconBackground === "#dbeafe" ? "2px" : "50%",
                            background: iconIndicator,
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: "2px" }}>
                          {title}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: subtitleColor
                              ? subtitleColor
                              : disabled
                              ? "#9ca3af"
                              : "#6b7280",
                            lineHeight: "1.4",
                          }}
                        >
                          {subtitle}
                        </div>
                      </div>
                    </button>
                  );

                  const renderDivider = (key: string) => (
                    <div
                      key={key}
                      style={{
                        height: "1px",
                        background: "#f3f4f6",
                        margin: "4px 8px",
                      }}
                    />
                  );

                  const sections: Array<{
                    key: "draft" | "template" | "question";
                    createTitle: string;
                    createSubtitle: string;
                    updateTitle: string;
                    updateSubtitle: string;
                    duplicateTitle: string;
                    duplicateSubtitle: string;
                    primaryIcon: { bg: string; indicator: string };
                    duplicateIcon: { bg: string; indicator: string };
                    onPrimary: () => void;
                    onDuplicate: () => void;
                    primaryDisabled?: boolean;
                  }> = [
                    {
                      key: "draft",
                      createTitle: t("save.draft.createTitle"),
                      createSubtitle: t("save.draft.createSubtitle"),
                      updateTitle: t("save.draft.updateTitle"),
                      updateSubtitle: t("save.draft.updateSubtitle"),
                      duplicateTitle: t("save.draft.duplicateTitle"),
                      duplicateSubtitle: t("save.draft.duplicateSubtitle"),
                      primaryIcon: { bg: "#fef3c7", indicator: "#f59e0b" },
                      duplicateIcon: { bg: "#fde68a", indicator: "#d97706" },
                      onPrimary: () => saveAsDraft(),
                      onDuplicate: () => saveAsDraft({ forceCreate: true }),
                    },
                    {
                      key: "template",
                      createTitle: t("save.template.createTitle"),
                      createSubtitle: t("save.template.createSubtitle"),
                      updateTitle: t("save.template.updateTitle"),
                      updateSubtitle: t("save.template.updateSubtitle"),
                      duplicateTitle: t("save.template.duplicateTitle"),
                      duplicateSubtitle: t("save.template.duplicateSubtitle"),
                      primaryIcon: { bg: "#dbeafe", indicator: "#3b82f6" },
                      duplicateIcon: { bg: "#bfdbfe", indicator: "#2563eb" },
                      onPrimary: () => {
                        // If updating an existing template, use current title without showing modal
                        const isUpdatingTemplate =
                          activeQuestionId &&
                          persistedQuestionType === "template";
                        if (isUpdatingTemplate && canvas.question?.title) {
                          saveAsTemplate({
                            forceCreate: false,
                            name: canvas.question.title,
                          });
                        } else {
                          // New template or no title - show modal to get name
                          setTemplateNameModalOptions({ isDuplicate: false });
                          setTemplateNameModalOpen(true);
                        }
                      },
                      onDuplicate: () => {
                        setTemplateNameModalOptions({ isDuplicate: true });
                        setTemplateNameModalOpen(true);
                      },
                    },
                    {
                      key: "question",
                      createTitle: t("save.normal.createTitle"),
                      createSubtitle: normalSaveDisabled
                        ? t("save.normal.createSubtitleDisabled")
                        : t("save.normal.createSubtitle"),
                      updateTitle: t("save.normal.updateTitle"),
                      updateSubtitle: normalSaveDisabled
                        ? t("save.normal.createSubtitleDisabled")
                        : t("save.normal.updateSubtitle"),
                      duplicateTitle: t("save.normal.duplicateTitle"),
                      duplicateSubtitle: t("save.normal.duplicateSubtitle"),
                      primaryIcon: { bg: "#d1fae5", indicator: "#10b981" },
                      duplicateIcon: { bg: "#bbf7d0", indicator: "#0d9488" },
                      onPrimary: () => saveNormal(),
                      onDuplicate: () => saveNormal({ forceCreate: true }),
                      primaryDisabled: normalSaveDisabled,
                    },
                  ];

                  const menuItems: React.ReactNode[] = [];

                  sections.forEach((section, index) => {
                    const isPersisted = persistedQuestionType === section.key;
                    const showDuplicate = isPersisted && hasExistingQuestion;
                    const primaryDisabled = Boolean(section.primaryDisabled);
                    const subtitleColor =
                      section.key === "question" && section.primaryDisabled
                        ? "#dc2626"
                        : undefined;

                    const isUpdateDisabled = showDuplicate && !hasUnsavedChanges;

                    menuItems.push(
                      renderMenuItem({
                        key: `${section.key}-primary`,
                        title: showDuplicate
                          ? section.updateTitle
                          : section.createTitle,
                        subtitle: isUpdateDisabled
                          ? t("save.updateDisabledHint")
                          : showDuplicate
                          ? section.updateSubtitle
                          : section.createSubtitle,
                        iconBackground: section.primaryIcon.bg,
                        iconIndicator: section.primaryIcon.indicator,
                        onClick: section.onPrimary,
                        disabled: primaryDisabled || isUpdateDisabled,
                        subtitleColor: isUpdateDisabled ? undefined : subtitleColor,
                      })
                    );

                    if (showDuplicate) {
                      menuItems.push(
                        renderMenuItem({
                          key: `${section.key}-duplicate`,
                          title: section.duplicateTitle,
                          subtitle: !hasUnsavedChanges
                            ? t("save.duplicateDisabledHint")
                            : section.duplicateSubtitle,
                          iconBackground: section.duplicateIcon.bg,
                          iconIndicator: section.duplicateIcon.indicator,
                          onClick: section.onDuplicate,
                          disabled:
                            !hasUnsavedChanges ||
                            (section.key === "question"
                              ? Boolean(section.primaryDisabled)
                              : false),
                        })
                      );
                    }

                    if (index < sections.length - 1) {
                      menuItems.push(renderDivider(`divider-${index}`));
                    }
                  });

                  return (
                    <>
                      <div
                        style={{
                          position: "fixed",
                          top: "0",
                          left: "0",
                          right: "0",
                          bottom: "0",
                          zIndex: 9999,
                        }}
                        onClick={() => setSaveMenuOpen(false)}
                      />
                      <div
                        className="qe-dropdown__menu"
                        style={{
                          position: "absolute",
                          top: "60px",
                          right: "20px",
                          background: "white",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          boxShadow:
                            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                          minWidth: "260px",
                          zIndex: 10000,
                          padding: "6px",
                          overflow: "hidden",
                        }}
                      >
                        {menuItems}
                      </div>
                    </>
                  );
                })(),
                document.body
              )}
          </div>
          {isQuestionLoading ? (
            <span
              style={{
                marginLeft: "12px",
                fontSize: "12px",
                color: "#2563eb",
                whiteSpace: "nowrap",
              }}
            >
              {t("header.questionLoading")}
            </span>
          ) : null}
        </div>
      </header>

      <div className="qe-body">
        <aside className="qe-rail qe-rail--left">
          <div className="qe-panel qe-panel--question-type">
            <div className="qe-panel__title">{t("panel.questionType.title")}</div>
            <div className="qe-panel__content qe-panel__content--padded">
              {isQuestionTypesLoading ? (
                <p className="qe-form__help">{t("panel.questionType.loading")}</p>
              ) : questionTypes.length === 0 ? (
                <p className="qe-form__help">{t("panel.questionType.empty")}</p>
              ) : (
                <>
                  <select
                    className="qe-input"
                    value={selectQuestionTypeValue}
                    onChange={(event) => {
                      const nextValue = event.target.value;

                      const selectedType = questionTypes.find(
                        (type) => String(type.id) === nextValue
                      );

                      if (selectedType) {
                        handleSelectQuestionType(selectedType);
                      }
                    }}
                  >
                    {!isActiveQuestionTypeInList && activeQuestionTypeId ? (
                      <option value={activeQuestionTypeId} disabled>
                        {activeQuestionTypeName || t("panel.questionType.selectedFallback")}
                      </option>
                    ) : null}
                    {questionTypes.map((type) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.displayName || type.name || t("panel.questionType.unnamed")}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>

          <div className="qe-panel qe-panel--elements">
            <div className="qe-panel__title">{t("panel.elements.title")}</div>
            <div className="qe-panel__content qe-panel__content--scroll">
              {!hasActiveQuestionType ? (
                <p className="qe-form__help">
                  {t("panel.elements.noTypeHelp")}
                </p>
              ) : null}
              {filteredLibrarySections.map((section) => (
                <div className="qe-panel__section" key={section.title}>
                  <div
                    className="qe-panel__subtitle"
                    style={
                      section.id === "formElements"
                        ? {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }
                        : undefined
                    }
                  >
                    <span>{section.title}</span>
                    {section.id === "formElements" ? (
                      <button
                        type="button"
                        className="qe-button qe-button--ghost qe-icon-button qe-button--small"
                        onClick={() => setIsFormVisibilityOpen(true)}
                        title="Soru tiplerine göre form elemanı görünürlüğünü ayarla"
                        aria-label="Form elemanı görünürlüğü ayarları"
                      >
                        ⚙
                      </button>
                    ) : null}
                  </div>
                  <div className="qe-chip-grid">
                    {section.items.map((item) => {
                      const templateKey = item.template;
                      const isDisabled = item.disabled || !templateKey;
                      const isDraggable = Boolean(templateKey) && !isDisabled;

                      return (
                        <button
                          key={item.id}
                          className="qe-pill"
                          type="button"
                          disabled={isDisabled}
                          draggable={isDraggable}
                          onClick={() => {
                            if (templateKey && !isDisabled) {
                              handleAddElement(templateKey);
                            }
                          }}
                          onDragStart={(event) => {
                            if (!templateKey || isDisabled) {
                              return;
                            }
                            event.dataTransfer.setData(
                              TEMPLATE_MIME,
                              templateKey
                            );
                            event.dataTransfer.effectAllowed = "copy";
                          }}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* TEMPORARY: Varlık Kütüphanesi geçici olarak kapatıldı */}
          {/* <div className="qe-panel qe-panel--assets">
            <div className="qe-panel__title">Varlık Kütüphanesi</div>
            <div className="qe-panel__content qe-panel__content--scroll">
              {assets.map((asset) => (
                <div className="qe-asset" key={asset.id}>
                  <div
                    className={`qe-asset__thumb qe-asset__thumb--${asset.type}`}
                  />
                  <div>
                    <div className="qe-asset__name">{asset.name}</div>
                    {asset.meta ? (
                      <div className="qe-asset__meta">{asset.meta}</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div> */}

          {/* ==================== TEMPORARY: QUESTION EXAMPLES ==================== */}
          {/* Bu bölüm geçicidir ve kolayca silinebilir. */}
          {/* Silmek için bu yorum bloğu ile bir sonraki yorum bloğu arasındaki tüm kodu silin. */}
          {/* <div className="qe-panel qe-panel--assets" style={{ flex: 1 }}>
            <div className="qe-panel__title">Soru Örnekleri</div>
            <div
              className="qe-panel__content qe-panel__content--scroll"
              style={{ padding: "16px" }}
            >
              {[
                { id: "8", label: "Örnek Soru 1" },
                { id: "7", label: "Örnek Soru 2" },
                { id: "9", label: "Örnek Soru 3" },
                { id: "10", label: "Örnek Soru 4" },
                { id: "11", label: "Örnek Soru 5" },
                { id: "31", label: "Örnek Soru 6" },
                { id: "32", label: "Örnek Soru 7" },
                { id: "33", label: "Örnek Soru 8" },
                { id: "35", label: "Örnek Soru 9" },
                { id: "566", label: "Örnek Soru 10" },
                { id: "569", label: "Örnek Soru 11" },
              ].map((example) => (
                <button
                  key={example.id}
                  type="button"
                  onClick={() => {
                    // URL parametresini güncelle
                    const params = new URLSearchParams(window.location.search);
                    params.set("questionId", example.id);
                    const nextUrl = `${
                      window.location.pathname
                    }?${params.toString()}`;
                    window.history.pushState({}, "", nextUrl);

                    // Soruyu yükle
                    setActiveQuestionId(example.id);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    marginBottom: "8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    backgroundColor:
                      activeQuestionId === example.id ? "#e1e9ff" : "#ffffff",
                    color: "#1f2430",
                    fontSize: "14px",
                    fontWeight: activeQuestionId === example.id ? 600 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (activeQuestionId !== example.id) {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeQuestionId !== example.id) {
                      e.currentTarget.style.backgroundColor = "#ffffff";
                    }
                  }}
                >
                  {example.label}
                </button>
              ))}
            </div>
          </div> */}
          {/* ==================== END TEMPORARY: QUESTION EXAMPLES ==================== */}

          {/* ==================== TEMPLATES BUTTON ==================== */}
          <div style={{ padding: "16px", borderTop: "1px solid #e5e7eb" }}>
            <button
              type="button"
              onClick={async () => {
                // Her açılışta en güncel şablonları görmek için definitions'ı yeniden çek
                await fetchDefinitions();
                setTemplatesModalOpen(true);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                color: "#1f2430",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f3f4f6";
                e.currentTarget.style.borderColor = "#d1d5db";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.borderColor = "#e5e7eb";
              }}
            >
              {t("panel.templates.button")}
            </button>
          </div>
          {/* ==================== END TEMPLATES BUTTON ==================== */}
        </aside>

        <main className="qe-stage" style={stageStyle} ref={stageRef}>
          <Toolbar
            selectedElement={selectedElement}
            selectedElementIds={selectedElementIds}
            elements={elements}
            onUndo={() => dispatch({ type: "undo" })}
            onRedo={() => dispatch({ type: "redo" })}
            onUpdateElement={handleUpdateElement}
            onAlignElements={(elementIds, alignment) => {
              dispatch({ type: "align-elements", elementIds, alignment });
            }}
            onAlignElementsToCanvas={(elementIds, alignment) => {
              dispatch({
                type: "align-elements-to-canvas",
                elementIds,
                alignment,
              });
            }}
            onGroupElements={(elementIds) => {
              dispatch({ type: "group-elements", elementIds });
            }}
            onUngroupElement={(groupId) => {
              dispatch({ type: "ungroup-element", groupId });
            }}
            showRulers={showCanvasRulers}
            showGuides={showCanvasGuides}
            hasGuides={canvasGuides.length > 0}
            onToggleRulers={() => setShowCanvasRulers((current) => !current)}
            onToggleGuides={() => setShowCanvasGuides((current) => !current)}
            onAddVerticalGuide={() => {
              setShowCanvasRulers(true);
              addCanvasGuide("vertical", dynamicCanvasSize.width / 2);
            }}
            onAddHorizontalGuide={() => {
              setShowCanvasRulers(true);
              addCanvasGuide("horizontal", dynamicCanvasSize.height / 2);
            }}
            onClearGuides={() => {
              setCanvasGuides([]);
              setActiveCanvasGuidePreset("");
            }}
            activeGuidePreset={activeCanvasGuidePreset}
            onApplyGuidePreset={applyCanvasGuidePreset}
            getShortcutsForElement={getShortcutsForElement}
          />

          {isQuestionSettingsBarVisible && (
            <QuestionSettingsBar
              canvas={canvas}
              onUpdateQuestion={handleUpdateQuestionMeta}
              lessons={lessons || []}
              grades={grades || []}
              subjects={subjects || []}
              questionTypes={questionTypes || []}
              isLoading={isDefinitionsLoading}
            />
          )}

          <div
            className={`qe-canvas-shell ${
              isLocalMediaDragActive ? "qe-canvas-shell--media-drop" : ""
            }`}
            onMouseDown={(e) => {
              // Deselect when clicking anywhere in canvas area (not on elements)
              const target = e.target as HTMLElement;
              if (
                target.classList.contains("qe-canvas-shell") ||
                target.classList.contains("qe-canvas") ||
                target.classList.contains("qe-canvas__hidden-area")
              ) {
                // Start selection box if clicking on canvas (not on elements)
                if (
                  target.classList.contains("qe-canvas-shell") ||
                  target.classList.contains("qe-canvas")
                ) {
                  const canvasRect = canvasRef.current?.getBoundingClientRect();
                  if (canvasRect) {
                    // Account for canvas padding and inner padding
                    const innerPaddingLeft = canvas.innerPadding?.left || 0;
                    const innerPaddingTop = canvas.innerPadding?.top || 0;
                    const startX =
                      (e.clientX - canvasRect.left) / zoom - innerPaddingLeft;
                    const startY =
                      (e.clientY - canvasRect.top) / zoom - innerPaddingTop;
                    setSelectionBox({
                      startX,
                      startY,
                      endX: startX,
                      endY: startY,
                    });
                    setIsSelecting(true);
                  }
                } else {
                  handleSelectElement(null);
                }
              }
            }}
            onDragOver={handleDragOverCanvas}
            onDragLeave={handleDragLeaveCanvas}
            onDrop={handleDropOnCanvas}
          >
            <div
              ref={canvasRef}
              className="qe-canvas"
              style={{
                position: "relative",
                width: `${dynamicCanvasSize.width}px`,
                minHeight: `${dynamicCanvasSize.height}px`,
                backgroundColor: canvas.background,
                marginLeft: `${canvas.padding?.x || 32}px`,
                marginTop: `${canvas.padding?.y || 32}px`,
                padding: canvas.innerPadding
                  ? `${canvas.innerPadding.top}px ${canvas.innerPadding.right}px ${canvas.innerPadding.bottom}px ${canvas.innerPadding.left}px`
                  : undefined,
                boxSizing: "border-box",
              }}
            >
              {showCanvasRulers ? (
                <>
                  <span className="qe-canvas-ruler__corner" aria-hidden="true" />
                  <button
                    type="button"
                    className="qe-canvas-ruler qe-canvas-ruler--horizontal"
                    onPointerDown={(event) =>
                      handleCanvasRulerPointerDown("vertical", event)
                    }
                    title={t("guides.addVerticalFromRuler")}
                    aria-label={t("guides.addVerticalFromRuler")}
                  >
                    {getRulerLabelPositions(dynamicCanvasSize.width).map(
                      (position) => (
                        <span
                          key={`horizontal-ruler-${position}`}
                          className="qe-canvas-ruler__label"
                          style={{ left: `${position}px` }}
                        >
                          {position}
                        </span>
                      )
                    )}
                  </button>
                  <button
                    type="button"
                    className="qe-canvas-ruler qe-canvas-ruler--vertical"
                    onPointerDown={(event) =>
                      handleCanvasRulerPointerDown("horizontal", event)
                    }
                    title={t("guides.addHorizontalFromRuler")}
                    aria-label={t("guides.addHorizontalFromRuler")}
                  >
                    {getRulerLabelPositions(dynamicCanvasSize.height).map(
                      (position) => (
                        <span
                          key={`vertical-ruler-${position}`}
                          className="qe-canvas-ruler__label"
                          style={{ top: `${position}px` }}
                        >
                          {position}
                        </span>
                      )
                    )}
                  </button>
                </>
              ) : null}
              {showCanvasGuides
                ? canvasGuides.map((guide) => (
                    <div
                      key={guide.id}
                      role="button"
                      tabIndex={0}
                      className={`qe-canvas-guide qe-canvas-guide--${guide.orientation}`}
                      style={
                        guide.orientation === "vertical"
                          ? { left: `${guide.position}px` }
                          : { top: `${guide.position}px` }
                      }
                      title={t("guides.moveOrRemove")}
                      aria-label={t("guides.moveOrRemove")}
                      onPointerDown={(event) => {
                        if (event.pointerType === "mouse" && event.button !== 0) {
                          return;
                        }
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.setPointerCapture(event.pointerId);
                      }}
                      onPointerMove={(event) => {
                        if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
                          return;
                        }
                        updateCanvasGuide(
                          guide.id,
                          getCanvasGuidePosition(
                            guide.orientation,
                            event.clientX,
                            event.clientY
                          )
                        );
                      }}
                      onPointerUp={(event) => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                          event.currentTarget.releasePointerCapture(event.pointerId);
                        }
                      }}
                      onPointerCancel={(event) => {
                        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                          event.currentTarget.releasePointerCapture(event.pointerId);
                        }
                      }}
                      onDoubleClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        removeCanvasGuide(guide.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Delete" || event.key === "Backspace") {
                          event.preventDefault();
                          removeCanvasGuide(guide.id);
                        }
                      }}
                    />
                  ))
                : null}
              {shouldRenderHiddenArea ? (
                <>
                  <div
                    className="qe-canvas__hidden-area qe-canvas__hidden-area--top"
                    style={{ height: hiddenAreaSize }}
                  />
                  <div
                    className="qe-canvas__hidden-area qe-canvas__hidden-area--bottom"
                    style={{ height: hiddenAreaSize }}
                  />
                </>
              ) : null}
              {/* Selection box overlay */}
              {selectionBox && (
                <div
                  style={{
                    position: "absolute",
                    left: `${
                      (canvas.innerPadding?.left || 0) +
                      Math.min(selectionBox.startX, selectionBox.endX)
                    }px`,
                    top: `${
                      (canvas.innerPadding?.top || 0) +
                      Math.min(selectionBox.startY, selectionBox.endY)
                    }px`,
                    width: `${Math.abs(
                      selectionBox.endX - selectionBox.startX
                    )}px`,
                    height: `${Math.abs(
                      selectionBox.endY - selectionBox.startY
                    )}px`,
                    border: "2px dashed #3b82f6",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    pointerEvents: "none",
                    zIndex: 10000,
                  }}
                />
              )}
              {/* Matching connections SVG overlay - SADECE PREVIEW MODUNDA GÖRÜNÜR, CANVAS MODUNDA GÖRÜNMEZ */}
              {elements.map((element) => (
                <CanvasElement
                  key={element.id}
                  element={element}
                  isSelected={selectedElementIds.includes(element.id)}
                  selectedElementIds={selectedElementIds}
                  onSelect={handleSelectElement}
                  onDrag={handleMoveElement}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onResize={handleResizeElement}
                  onContextMenu={handleContextMenu}
                  onUpdate={handleUpdateElementData}
                  canvasSize={dynamicCanvasSize}
                  canvasExpandable={canvas.expandable}
                  canvasLockWidth={canvas.lockWidth}
                  canvasInnerPadding={canvas.innerPadding}
                  zoom={zoom}
                  allElements={elements}
                  onDropTargetChange={handleDropTargetChange}
                  onChoiceChange={handleChoiceChange}
                  formState={formState}
                />
              ))}
            </div>
          </div>
        </main>

        <aside className="qe-rail qe-rail--right">
          <div className="qe-panel qe-panel--properties">
            <div className="qe-panel__title">{t("panel.properties.title")}</div>
            <div className="qe-panel__content qe-panel__content--scroll">
              <ElementPropertiesPanel
                element={selectedElement}
                elements={elements}
                selectedElementId={selectedElementId}
                onMove={handleMoveElement}
                onResize={handleResizeElement}
                onUpdate={handleUpdateElement}
                onDelete={handleDeleteSelected}
                onSelect={handleSelectElement}
                onReorder={(id, direction) =>
                  dispatch({
                    type: "reorder-element",
                    elementId: id,
                    direction,
                  })
                }
                onAddShortcut={addShortcut}
                getShortcutsForElement={getShortcutsForElement}
                onRemoveShortcut={removeShortcut}
              />
              <InteractionPanel
                element={selectedElement}
                elements={elements}
                onUpdateData={handleUpdateElementData}
              />
            </div>
          </div>
        </aside>
      </div>

      {isSettingsOpen ? (
        <SettingsModal
          canvas={canvas}
          defaults={DEFAULT_CANVAS_SETTINGS}
          onClose={closeSettings}
          onSave={handleSaveSettings}
          onReset={handleResetSettings}
          onAlignCanvas={handleAlignCanvas}
        />
      ) : null}
      {isFormVisibilityOpen ? (
        <FormElementVisibilityModal
          questionTypes={questionTypes}
          formElementItems={configurableFormElementItems}
          overrides={formVisibility}
          resolveDefault={resolveDefaultAllowedTemplates}
          onSave={handleSaveFormVisibility}
          onClose={() => setIsFormVisibilityOpen(false)}
        />
      ) : null}
      {isAnswerKeyOpen ? (
        <AnswerKeyModal
          canvas={canvas}
          elements={elements}
          onClose={closeAnswerKey}
          onSave={handleSaveQuestionSettings}
          onUpdateElement={handleUpdateElement}
        />
      ) : null}
      {isTemplatesModalOpen ? (
        <TemplatesModal
          templates={templates}
          onClose={() => setTemplatesModalOpen(false)}
          onSelect={handleTemplateSelect}
        />
      ) : null}
      {isTemplateNameModalOpen && templateNameModalOptions ? (
        <TemplateNameModal
          onClose={() => {
            setTemplateNameModalOpen(false);
            setTemplateNameModalOptions(null);
          }}
          onConfirm={({ name, tags }) => {
            saveAsTemplate({
              forceCreate: templateNameModalOptions.isDuplicate,
              name,
              tags,
            });
            setTemplateNameModalOpen(false);
            setTemplateNameModalOptions(null);
          }}
          isDuplicate={templateNameModalOptions.isDuplicate}
          availableTags={definitionTags}
        />
      ) : null}
      {pendingQuestionType ? (
        <QuestionTypeChangeConfirmModal
          currentQuestionType={(() => {
            const currentTypeId = canvas.question?.questionType?.id;
            if (!currentTypeId) return undefined;
            const matched = questionTypes.find(
              (t) => String(t.id) === String(currentTypeId)
            );
            return (
              matched?.displayName ||
              matched?.name ||
              canvas.question?.questionType?.name
            );
          })()}
          newQuestionType={pendingQuestionType}
          elementCount={elements.length}
          onConfirm={handleConfirmQuestionTypeChange}
          onCancel={handleCancelQuestionTypeChange}
        />
      ) : null}
      {saveSuccessModal.isOpen ? (
        <SaveSuccessModal
          message={saveSuccessModal.message}
          onContinueEditing={handleContinueEditing}
          onRedirect={handleRedirectToQuestions}
        />
      ) : null}
      {isPreviewOpen ? (
        <FullScreenPreview
          elements={elements}
          canvas={{ ...canvas, ...dynamicCanvasSize }}
          onClose={closePreview}
        />
      ) : null}
      {contextMenu ? (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={[
            ...(contextMenu.hasTextSelection
              ? [
                  {
                    label: t("contextMenu.style"),
                    children: [
                      {
                        label: t("contextMenu.bold"),
                        onClick: () => {
                          window.dispatchEvent(
                            new CustomEvent("rich-text-format", {
                              detail: { command: "bold" },
                            })
                          );
                        },
                      },
                      {
                        label: t("contextMenu.italic"),
                        onClick: () => {
                          window.dispatchEvent(
                            new CustomEvent("rich-text-format", {
                              detail: { command: "italic" },
                            })
                          );
                        },
                      },
                      {
                        label: t("contextMenu.underline"),
                        onClick: () => {
                          window.dispatchEvent(
                            new CustomEvent("rich-text-format", {
                              detail: { command: "underline" },
                            })
                          );
                        },
                      },
                    ],
                  },
                ]
              : []),
            ...(() => {
              const element = elements.find(el => el.id === contextMenu.elementId);
              const items: Array<{
                label: string;
                danger?: boolean;
                onClick: () => void;
              }> = [];
              if (element?.type === "text") {
                items.push({
                  label: t("contextMenu.textToSpeech"),
                  onClick: () => {
                    handleOpenTextToSpeech(contextMenu.elementId);
                    handleCloseContextMenu();
                  },
                });
              }
              if (
                element?.type === "audio" &&
                Boolean(
                  (element.data as { generatedFromTextElementId?: string })
                    ?.generatedFromTextElementId
                )
              ) {
                items.push({
                  label: t("contextMenu.placeBehindText"),
                  onClick: () => {
                    handlePlaceAudioBehindText(contextMenu.elementId);
                    handleCloseContextMenu();
                  },
                });
              }
              if (canPermanentlyDeleteAudioElement(element)) {
                items.push({
                  label: t("contextMenu.permanentDelete"),
                  danger: true,
                  onClick: () => {
                    handlePermanentDeleteAudio(contextMenu.elementId);
                    handleCloseContextMenu();
                  },
                });
              }
              if (isMathTypeElement(element)) {
                items.push({
                  label: t("contextMenu.openInEditor"),
                  onClick: () => {
                    setMathTypeModalState({ isOpen: true, elementId: contextMenu.elementId });
                    handleCloseContextMenu();
                  },
                });
              }
              return items;
            })(),
            {
              label: t("contextMenu.duplicate"),
              onClick: () => {
                handleCloneElement(contextMenu.elementId);
                handleCloseContextMenu();
              },
            },
            {
              label: t("contextMenu.delete"),
              danger: true,
              onClick: () => {
                // Check if this is a cell element
                let isCellElement = false;
                for (const tableElement of elements) {
                  if (tableElement.type === "table") {
                    const cellElements = (tableElement.data as any)
                      .cellElements as
                      | Record<string, EditorElement>
                      | undefined;
                    if (cellElements && cellElements[contextMenu.elementId]) {
                      isCellElement = true;
                      const cells = (tableElement.data as any).cells as
                        | any[]
                        | undefined;

                      // Find which cell contains this element
                      const parentCell = cells?.find((cell: any) =>
                        cell.elements?.includes(contextMenu.elementId)
                      );

                      if (parentCell) {
                        // Remove element from cell and cellElements
                        const updatedCells = cells!.map((cell: any) =>
                          cell.id === parentCell.id
                            ? {
                                ...cell,
                                elements: cell.elements.filter(
                                  (id: string) => id !== contextMenu.elementId
                                ),
                              }
                            : cell
                        );

                        const updatedCellElements = { ...cellElements };
                        delete updatedCellElements[contextMenu.elementId];

                        dispatch({
                          type: "update-element",
                          elementId: tableElement.id,
                          changes: {
                            data: {
                              ...tableElement.data,
                              cellElements: updatedCellElements,
                              cells: updatedCells,
                              selectedCellElementId: undefined, // Clear selection
                            },
                          },
                        });
                      }
                      break;
                    }
                  }
                }

                // If not a cell element, delete normally
                if (!isCellElement) {
                  dispatch({
                    type: "delete-element",
                    elementId: contextMenu.elementId,
                  });
                }

                handleCloseContextMenu();
              },
            },
          ]}
          onClose={handleCloseContextMenu}
        />
      ) : null}

      {textToSpeechElementId ? (() => {
        const element = elements.find(
          (candidate) => candidate.id === textToSpeechElementId
        );
        return (
          <TextToSpeechModal
            text={getElementSpeechText(element)}
            isSubmitting={isTextToSpeechSubmitting}
            onClose={() => {
              if (!isTextToSpeechSubmitting) {
                setTextToSpeechElementId(null);
              }
            }}
            onSubmit={handleSubmitTextToSpeech}
          />
        );
      })() : null}

      {/* MathType Editor Modal */}
      {mathTypeModalState.isOpen && mathTypeModalState.elementId && (() => {
        const element = elements.find(el => el.id === mathTypeModalState.elementId);
        if (!element || !isMathTypeElement(element)) return null;

        return (
          <MathTypeEditorModal
            isOpen={mathTypeModalState.isOpen}
            initialValue={
              (element.data.style as any)?.mathml ||
              (element.data.style as any)?.latex ||
              ""
            }
            onClose={() => setMathTypeModalState({ isOpen: false, elementId: null })}
            onSave={(mathml: string) => {
              dispatch({
                type: "update-element",
                elementId: mathTypeModalState.elementId!,
                changes: {
                  data: {
                    ...element.data,
                    style: {
                      ...(element.data.style || {}),
                      mathml: mathml,
                    },
                  },
                },
              });
              setMathTypeModalState({ isOpen: false, elementId: null });
            }}
          />
        );
      })()}
    </div>
  );
};

export default EditorLayout;
